(() => {
  'use strict';

  const body = document.body;
  if (!body) return;

  const locale = body.dataset.labLocale || document.documentElement.lang || 'en';
  const isRussian = String(locale).toLowerCase().startsWith('ru');
  const rootUrl = String(body.dataset.labRootUrl || '/').replace(/\/?$/, '/');
  const currency = body.dataset.labCurrency || 'USD';
  const catalogFeedUrl = body.dataset.labCatalogFeedUrl || `${rootUrl}collections/all/products.json?limit=250`;
  const contactUrl = body.dataset.labContactUrl || `${rootUrl}pages/contact`;
  const text = (english, russian) => (isRussian ? russian : english);

  const KEYS = {
    visit: 'fivemLabVisitV2',
    visitSession: 'fivemLabVisitSessionV2',
    updatesSeen: 'fivemLabUpdatesSeenV2',
    hiddenStudios: 'fivemLabHiddenStudioUpdatesV1',
    savedSearches: 'fivemLabSavedSearchesV1',
    catalogSession: 'fivemLabCatalogSessionV2',
    catalogResume: 'fivemLabCatalogResumeV1',
    productCache: 'fivemLabProductCacheV2',
    serverBuild: 'fivemLabServerBuildV1',
    compare: 'fivemLabCompareV1',
    watched: 'fivemLabWatchedResourcesV1',
    resourceSignals: 'fivemLabResourceSignalsV1',
    saved: 'labSavedResources',
    interests: 'labInterestsV1',
    followedStudios: 'labFollowedStudiosV1'
  };

  const readJson = (storage, key, fallback) => {
    try {
      const value = JSON.parse(storage.getItem(key) || 'null');
      return value === null ? fallback : value;
    } catch (error) {
      return fallback;
    }
  };

  const writeJson = (storage, key, value) => {
    try {
      storage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  };

  const removeStored = (storage, key) => {
    try {
      storage.removeItem(key);
    } catch (error) {
      // The feature continues for the current page when storage is unavailable.
    }
  };

  const publish = (name, detail = {}) => {
    const eventName = `fivem_lab_${name}`;
    const payload = { ...detail, pagePath: window.location.pathname };
    try {
      const result = window.Shopify?.analytics?.publish?.(eventName, payload);
      if (result?.catch) result.catch(() => {});
    } catch (error) {
      // Analytics must never block the storefront.
    }
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: eventName, ...payload });
    document.dispatchEvent(new CustomEvent(`lab:${name}`, { detail: payload }));
  };

  let toastTimer;
  const showToast = (message) => {
    let toast = document.querySelector('[data-lab-retention-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'lab-retention-toast';
      toast.dataset.labRetentionToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.append(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/&amp;/g, ' ')
    .replace(/[^a-z0-9а-яё]+/gi, ' ')
    .trim();

  const normalizeType = (value) => {
    const key = normalize(value);
    const aliases = new Map([
      ['mlos', 'mlo'], ['interiors', 'mlo'], ['interior', 'mlo'],
      ['scripts', 'script'], ['maps', 'map'], ['vehicles', 'vehicle'],
      ['cars', 'vehicle'], ['car', 'vehicle'], ['clothes', 'clothing'],
      ['eup', 'clothing'], ['bundles', 'bundle'], ['uis', 'ui']
    ]);
    return aliases.get(key) || key;
  };

  const asTime = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value < 100000000000 ? value * 1000 : value;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && String(value).trim() !== '') return numeric < 100000000000 ? numeric * 1000 : numeric;
    return Date.parse(value) || 0;
  };

  const getTags = (product) => {
    const tags = Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',');
    return tags.map((tag) => String(tag).trim()).filter(Boolean);
  };

  const toStringList = (value) => {
    if (Array.isArray(value)) return value.map(String).map((item) => item.trim()).filter(Boolean);
    if (value === null || value === undefined || value === '') return [];
    return String(value).split(/[|,]/).map((item) => item.trim()).filter(Boolean);
  };

  const getVariantPrices = (product) => {
    const prices = (Array.isArray(product?.variants) ? product.variants : [])
      .map((variant) => Number(variant?.price))
      .filter(Number.isFinite);
    return prices;
  };

  const detectFrameworks = (product) => {
    const explicit = [...toStringList(product?.frameworks), ...toStringList(product?.framework)]
      .map(normalizeType)
      .filter((framework) => ['qbox', 'qbcore', 'esx', 'standalone'].includes(framework));
    if (explicit.length) return [...new Set(explicit)];

    const source = normalize([
      product?.product_type,
      product?.type,
      product?.title,
      ...(Array.isArray(product?.collections) ? product.collections : []),
      ...getTags(product)
    ].filter(Boolean).join(' '));
    const frameworks = ['qbox', 'qbcore', 'esx'].filter((framework) => source.includes(framework));
    if (source.includes('standalone')) frameworks.push('standalone');
    return [...new Set(frameworks)];
  };

  const firstImage = (product) => {
    if (product?.image && typeof product.image === 'string') return product.image;
    if (product?.image?.src) return product.image.src;
    const image = Array.isArray(product?.images) ? product.images[0] : null;
    if (typeof image === 'string') return image;
    return image?.src || image?.url || '';
  };

  const normalizeProduct = (raw = {}) => {
    const variants = getVariantPrices(raw);
    const suppliedCents = Number(raw.priceCents);
    const priceCents = Number.isFinite(suppliedCents)
      ? suppliedCents
      : Math.round((variants.length ? Math.min(...variants) : Number(raw.price || 0)) * 100);
    const handle = String(raw.handle || '').trim();
    const explicitFrameworks = toStringList(raw.frameworks || raw.frameworkValues);
    const subcategories = toStringList(raw.subcategories || raw.subcategory);
    const languages = toStringList(raw.languages || raw.language);
    const product = {
      handle,
      title: String(raw.title || raw.displayTitle || text('Untitled resource', 'Ресурс без названия')),
      vendor: String(raw.vendor || raw.store || ''),
      type: String(raw.product_type || raw.type || raw.resourceType || ''),
      tags: getTags(raw),
      collections: Array.isArray(raw.collections) ? raw.collections.map(String) : [],
      url: String(raw.url || `${rootUrl}products/${encodeURIComponent(handle)}`),
      image: firstImage(raw),
      priceCents: Math.max(0, priceCents || 0),
      priceLabel: String(raw.priceLabel || ''),
      publishedAt: raw.publishedAt || raw.published_at || raw.createdAt || raw.created_at || '',
      updatedAt: raw.updatedAt || raw.updated_at || '',
      createdAt: raw.createdAt || raw.created_at || '',
      framework: raw.framework || '',
      frameworks: explicitFrameworks,
      subcategory: String(raw.subcategory || subcategories[0] || ''),
      subcategories,
      languages,
      version: String(raw.version || ''),
      creatorUrl: raw.creatorUrl || '',
      imageCount: Number(raw.imageCount ?? (Array.isArray(raw.images) ? raw.images.length : 0)),
      hasVideo: Boolean(raw.hasVideo),
      detailSnapshot: Boolean(raw.detailSnapshot),
      externalUrl: String(raw.externalUrl || ''),
      lastVerifiedAt: raw.lastVerifiedAt || '',
      verificationStatus: normalize(raw.verificationStatus || ''),
      descriptionKey: normalize(String(raw.descriptionKey || raw.body_html || raw.description || '')).slice(0, 600),
      dependencies: toStringList(raw.dependencies),
      incompatibleWith: toStringList(raw.incompatibleWith),
      taxonomy: raw.taxonomy && typeof raw.taxonomy === 'object' ? raw.taxonomy : {}
    };
    product.publishedMs = asTime(product.publishedAt) || asTime(product.createdAt);
    product.updatedMs = asTime(product.updatedAt);
    product.frameworks = detectFrameworks(product);
    product.availability = product.priceCents === 0 ? 'free' : 'paid';
    product.searchBlob = normalize([
      product.title,
      product.vendor,
      product.type,
      ...product.subcategories,
      ...product.languages,
      ...product.tags,
      ...product.frameworks
    ].join(' '));
    return product;
  };

  const moneyFormatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
  const formatPrice = (product) => product.priceLabel || (product.priceCents === 0
    ? text('Free', 'Бесплатно')
    : moneyFormatter.format(product.priceCents / 100));

  const relativeDate = (timestamp) => {
    const milliseconds = asTime(timestamp);
    if (!milliseconds) return '';
    const difference = milliseconds - Date.now();
    const absolute = Math.abs(difference);
    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (absolute < 3600000) return formatter.format(Math.round(difference / 60000), 'minute');
    if (absolute < 86400000) return formatter.format(Math.round(difference / 3600000), 'hour');
    if (absolute < 2592000000) return formatter.format(Math.round(difference / 86400000), 'day');
    return formatter.format(Math.round(difference / 2592000000), 'month');
  };

  let productCache = readJson(window.localStorage, KEYS.productCache, {});
  if (!productCache || typeof productCache !== 'object' || Array.isArray(productCache)) productCache = {};

  const rememberProducts = (products) => {
    products.filter((product) => product?.handle).forEach((product) => {
      const normalized = normalizeProduct(product);
      productCache[normalized.handle] = { ...normalized, cachedAt: Date.now() };
    });
    const entries = Object.entries(productCache)
      .sort((left, right) => Number(right[1]?.cachedAt || 0) - Number(left[1]?.cachedAt || 0))
      .slice(0, 350);
    productCache = Object.fromEntries(entries);
    writeJson(window.localStorage, KEYS.productCache, productCache);
  };

  const productFromCard = (card) => normalizeProduct({
    handle: card?.dataset.labHandle,
    title: card?.dataset.labDisplayTitle,
    vendor: card?.dataset.labVendor,
    product_type: card?.dataset.labProductType,
    tags: String(card?.dataset.labTags || '').split('|'),
    framework: card?.dataset.labFramework,
    frameworks: card?.dataset.labFrameworkValues,
    subcategories: card?.dataset.labSubcategory,
    languages: card?.dataset.labLanguageValues,
    version: card?.dataset.labVersion,
    externalUrl: card?.dataset.labExternalUrl,
    lastVerifiedAt: card?.dataset.labLastVerified,
    verificationStatus: card?.dataset.labVerificationStatus,
    taxonomy: {
      resourceType: card?.dataset.labCanonicalType === 'true',
      frameworks: card?.dataset.labCanonicalFrameworks === 'true',
      subcategories: card?.dataset.labCanonicalSubcategories === 'true',
      studio: card?.dataset.labCanonicalStudio === 'true',
      externalUrl: Boolean(card?.dataset.labExternalUrl),
      verification: Boolean(card?.dataset.labLastVerified)
    },
    url: card?.dataset.labUrl,
    image: card?.dataset.labImage,
    priceCents: Number(card?.dataset.labPriceCents || 0),
    priceLabel: card?.dataset.labPriceLabel,
    publishedAt: Number(card?.dataset.labPublished || card?.dataset.labCreated || 0),
    updatedAt: Number(card?.dataset.labUpdated || 0)
  });

  const viewedProductScript = document.querySelector('[data-lab-viewed-product]');
  let viewedProduct = null;
  if (viewedProductScript) {
    try {
      viewedProduct = normalizeProduct(JSON.parse(viewedProductScript.textContent || '{}'));
      if (viewedProduct.handle) rememberProducts([viewedProduct]);
    } catch (error) {
      viewedProduct = null;
    }
  }

  const collectVisibleCardProducts = (scope = document) => {
    const products = [...scope.querySelectorAll('[data-lab-resource-card]')]
      .map(productFromCard)
      .filter((product) => product.handle);
    if (products.length) rememberProducts(products);
    return products;
  };

  const sourcePromises = new Map();
  const fetchCatalog = (source = catalogFeedUrl, maxPages = 8) => {
    const sourceUrl = new URL(source, window.location.origin);
    sourceUrl.searchParams.set('limit', '250');
    const cacheKey = sourceUrl.pathname;
    if (sourcePromises.has(cacheKey)) return sourcePromises.get(cacheKey);

    const task = (async () => {
      if (cacheKey === new URL(catalogFeedUrl, window.location.origin).pathname) {
        const cached = readJson(window.sessionStorage, KEYS.catalogSession, null);
        if (cached?.storedAt && Date.now() - cached.storedAt < 120000 && Array.isArray(cached.products)) {
          const products = cached.products.map(normalizeProduct);
          rememberProducts(products);
          return products;
        }
      }

      const allProducts = [];
      for (let page = 1; page <= maxPages; page += 1) {
        const url = new URL(sourceUrl.toString());
        url.searchParams.set('page', String(page));
        url.searchParams.set('_lab_retention', String(Date.now()));
        const response = await fetch(url.toString(), {
          cache: 'no-store',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) {
          if (page > 1) break;
          throw new Error(`Retention catalog request failed with ${response.status}`);
        }
        const payload = await response.json();
        const pageProducts = Array.isArray(payload.products) ? payload.products.map(normalizeProduct) : [];
        allProducts.push(...pageProducts);
        if (pageProducts.length < 250) break;
      }

      const products = [...new Map(allProducts.map((product) => [product.handle, product])).values()];
      rememberProducts(products);
      if (cacheKey === new URL(catalogFeedUrl, window.location.origin).pathname) {
        writeJson(window.sessionStorage, KEYS.catalogSession, { storedAt: Date.now(), products });
      }
      return products;
    })().catch((error) => {
      console.error(error);
      const cachedProducts = Object.values(productCache).map(normalizeProduct);
      return cachedProducts.length ? cachedProducts : collectVisibleCardProducts();
    });

    sourcePromises.set(cacheKey, task);
    return task;
  };

  const initVisitSession = () => {
    const existingSession = readJson(window.sessionStorage, KEYS.visitSession, null);
    const sessionIsFresh = existingSession?.startedAt && Date.now() - Number(existingSession.startedAt) < 4 * 3600000;
    if (sessionIsFresh && existingSession?.previousVisitAt) return existingSession;
    const previous = readJson(window.localStorage, KEYS.visit, {});
    const now = Date.now();
    const session = { startedAt: now, previousVisitAt: Number(previous?.lastVisitAt || now) };
    writeJson(window.sessionStorage, KEYS.visitSession, session);
    writeJson(window.localStorage, KEYS.visit, { lastVisitAt: now });
    return session;
  };

  const visitSession = initVisitSession();
  let updatesSeenAt = Number(readJson(window.localStorage, KEYS.updatesSeen, 0) || 0);
  const updateThreshold = () => Math.max(Number(visitSession.previousVisitAt || 0), updatesSeenAt);

  const getInterests = () => {
    const value = readJson(window.localStorage, KEYS.interests, {});
    return {
      frameworks: Array.isArray(value?.frameworks) ? value.frameworks.map(normalizeType) : [],
      types: Array.isArray(value?.types) ? value.types.map(normalizeType) : [],
      price: ['free', 'paid'].includes(value?.price) ? value.price : 'any'
    };
  };

  const getFollowedStudios = () => {
    const studios = readJson(window.localStorage, KEYS.followedStudios, []);
    return Array.isArray(studios) ? studios.filter((studio) => studio?.name) : [];
  };

  const getHiddenStudios = () => {
    const value = readJson(window.localStorage, KEYS.hiddenStudios, []);
    return new Set(Array.isArray(value) ? value.map(normalize) : []);
  };

  const productMatchesInterests = (product) => {
    const interests = getInterests();
    const studios = getFollowedStudios();
    const hasPreferences = interests.frameworks.length || interests.types.length || interests.price !== 'any' || studios.length;
    if (!hasPreferences) return true;
    if (studios.some((studio) => normalize(studio.name) === normalize(product.vendor))) return true;
    if (interests.frameworks.some((framework) => product.frameworks.includes(framework))) return true;
    if (interests.types.some((type) => product.searchBlob.includes(type))) return true;
    return interests.price !== 'any' && product.availability === interests.price;
  };

  const interestScore = (product) => {
    const interests = getInterests();
    const followed = getFollowedStudios();
    let score = 0;
    if (followed.some((studio) => normalize(studio.name) === normalize(product.vendor))) score += 80;
    interests.frameworks.forEach((framework) => { if (product.frameworks.includes(framework)) score += 45; });
    interests.types.forEach((type) => { if (product.searchBlob.includes(type)) score += 35; });
    if (interests.price !== 'any' && product.availability === interests.price) score += 20;
    return score;
  };

  const createProductTile = (product, options = {}) => {
    const article = document.createElement('article');
    article.className = 'lab-retention-product';
    article.dataset.labRetentionProduct = product.handle;

    const media = document.createElement('a');
    media.className = 'lab-retention-product__media';
    media.href = product.url;
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';
      media.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.textContent = product.type || 'FiveM';
      media.append(placeholder);
    }

    const content = document.createElement('div');
    content.className = 'lab-retention-product__body';
    const heading = document.createElement('h3');
    const link = document.createElement('a');
    link.href = product.url;
    link.textContent = product.title;
    heading.append(link);
    const meta = document.createElement('p');
    meta.textContent = [product.vendor, product.type].filter(Boolean).join(' · ');
    const footer = document.createElement('div');
    const price = document.createElement('strong');
    price.textContent = formatPrice(product);
    footer.append(price);
    if (options.showDate) {
      const time = document.createElement('time');
      time.dateTime = product.publishedAt || '';
      const relative = relativeDate(product.publishedMs);
      time.textContent = text(`Added ${relative}`, `Добавлено ${relative}`);
      footer.append(time);
    }
    const actions = document.createElement('span');
    actions.className = 'lab-retention-product__actions';
    const buildButton = document.createElement('button');
    buildButton.type = 'button';
    buildButton.dataset.labBuildAdd = product.handle;
    buildButton.textContent = text('Add to build', 'В сборку');
    const view = document.createElement('a');
    view.href = product.url;
    view.textContent = text('View', 'Открыть');
    actions.append(buildButton, view);
    content.append(heading, meta, footer, actions);
    article.append(media, content);
    return article;
  };

  let latestCatalog = [];
  let refreshRetentionSurfaces = () => {};

  const countTrackedChanges = () => {
    const watched = readJson(window.localStorage, KEYS.watched, {});
    return Object.values(watched || {}).reduce((count, item) => {
      return count + (Array.isArray(item?.changes) ? item.changes.filter((change) => !change.seen).length : 0);
    }, 0);
  };

  const personalizedNewProducts = (products) => products
    .filter((product) => product.publishedMs > updateThreshold() && productMatchesInterests(product))
    .sort((left, right) => interestScore(right) - interestScore(left) || right.publishedMs - left.publishedMs);

  const updateHeaderCount = (products = latestCatalog) => {
    const newCount = personalizedNewProducts(products).length;
    const total = newCount + countTrackedChanges();
    document.querySelectorAll('[data-lab-header-update-count]').forEach((counter) => {
      counter.textContent = String(total > 99 ? '99+' : total);
      counter.toggleAttribute('hidden', total === 0);
      counter.setAttribute('aria-label', text(`${total} unread updates`, `${total} непрочитанных обновлений`));
    });
    document.querySelectorAll('[data-lab-my-stat="updates"], [data-lab-my-tab-count="updates"]').forEach((counter) => {
      counter.textContent = String(newCount);
      counter.toggleAttribute('hidden', counter.hasAttribute('data-lab-my-tab-count') && newCount === 0);
    });
  };

  const renderNewSinceVisit = (products = latestCatalog) => {
    const newProducts = personalizedNewProducts(products);
    document.querySelectorAll('[data-lab-new-since]').forEach((section) => {
      const target = section.querySelector('[data-lab-new-since-target]');
      const count = section.querySelector('[data-lab-new-since-count]');
      const limit = Math.max(1, Number(section.dataset.labNewSinceLimit || 8));
      if (!target || visitSession.previousVisitAt >= visitSession.startedAt || !newProducts.length) {
        section.hidden = true;
        return;
      }
      target.replaceChildren(...newProducts.slice(0, limit).map((product) => createProductTile(product, { showDate: true })));
      if (count) count.textContent = String(newProducts.length);
      const lastVisitLabel = section.querySelector('[data-lab-last-visit-label]');
      if (lastVisitLabel) {
        const relative = relativeDate(visitSession.previousVisitAt);
        lastVisitLabel.textContent = text(`Since your visit ${relative}`, `С прошлого визита: ${relative}`);
      }
      section.hidden = false;
    });
    updateHeaderCount(products);
  };

  const markUpdatesRead = () => {
    updatesSeenAt = Date.now();
    writeJson(window.localStorage, KEYS.updatesSeen, updatesSeenAt);
    updateHeaderCount();
    document.querySelectorAll('[data-lab-new-since]').forEach((section) => section.classList.add('is-seen'));
    showToast(text('Updates marked as seen.', 'Обновления отмечены как просмотренные.'));
    publish('updates_marked_seen');
  };

  const renderStudioUpdates = (products = latestCatalog) => {
    const followed = getFollowedStudios();
    const hidden = getHiddenStudios();
    const threshold = updateThreshold();
    const targets = document.querySelectorAll('[data-lab-studio-updates]');
    targets.forEach((target) => {
      const groups = followed
        .filter((studio) => !hidden.has(normalize(studio.name)))
        .map((studio) => {
          const followedAt = Number(studio.followedAt || 0);
          const studioProducts = products
            .filter((product) => normalize(product.vendor) === normalize(studio.name))
            .filter((product) => product.publishedMs > Math.max(followedAt, threshold))
            .sort((left, right) => right.publishedMs - left.publishedMs);
          return { studio, products: studioProducts };
        })
        .filter((group) => group.products.length);

      if (!followed.length) {
        target.innerHTML = `<p class="lab-my-empty">${text('Follow studios from resource pages to receive their updates here.', 'Подпишитесь на студии на страницах ресурсов, чтобы видеть их обновления.')}</p>`;
        return;
      }
      if (!groups.length) {
        target.innerHTML = `<p class="lab-my-empty">${text('You are up to date with followed studios.', 'Все обновления подписанных студий просмотрены.')}</p>`;
        return;
      }

      target.replaceChildren(...groups.map(({ studio, products: studioProducts }) => {
        const group = document.createElement('section');
        group.className = 'lab-studio-update-group';
        const header = document.createElement('div');
        header.className = 'lab-studio-update-group__head';
        const copy = document.createElement('div');
        const title = document.createElement('h3');
        title.textContent = text(
          `${studio.name} added ${studioProducts.length} ${studioProducts.length === 1 ? 'resource' : 'resources'}`,
          `${studio.name}: новых ресурсов ${studioProducts.length}`
        );
        const newest = document.createElement('p');
        newest.textContent = text(`Newest ${relativeDate(studioProducts[0].publishedMs)}`, `Последнее обновление ${relativeDate(studioProducts[0].publishedMs)}`);
        copy.append(title, newest);
        const actions = document.createElement('div');
        if (studio.url) {
          const studioLink = document.createElement('a');
          studioLink.href = studio.url;
          studioLink.textContent = text('Open studio', 'Открыть студию');
          actions.append(studioLink);
        }
        const hideButton = document.createElement('button');
        hideButton.type = 'button';
        hideButton.dataset.labHideStudioUpdates = studio.name;
        hideButton.textContent = text('Hide updates', 'Скрыть обновления');
        actions.append(hideButton);
        header.append(copy, actions);
        const list = document.createElement('div');
        list.className = 'lab-studio-update-group__products';
        list.append(...studioProducts.slice(0, 4).map((product) => createProductTile(product, { showDate: true })));
        group.append(header, list);
        return group;
      }));
    });

    document.querySelectorAll('[data-lab-hidden-studios]').forEach((container) => {
      const list = container.querySelector('[data-lab-hidden-studios-list]');
      const hiddenFollowed = followed.filter((studio) => hidden.has(normalize(studio.name)));
      if (!list || !hiddenFollowed.length) {
        container.hidden = true;
        return;
      }
      list.replaceChildren(...hiddenFollowed.map((studio) => {
        const row = document.createElement('div');
        row.className = 'lab-hidden-studio-row';
        const name = document.createElement('span');
        name.textContent = studio.name;
        const restore = document.createElement('button');
        restore.type = 'button';
        restore.dataset.labRestoreStudioUpdates = studio.name;
        restore.textContent = text('Show updates again', 'Снова показывать');
        row.append(name, restore);
        return row;
      }));
      container.hidden = false;
    });
  };

  const summarizeHomeState = (state) => {
    const labels = (state?.filters || []).map((filter) => filter.label).filter(Boolean);
    const parts = labels.slice(0, 3);
    if (labels.length > 3) parts.push(`+${labels.length - 3}`);
    if (state?.maximum) parts.push(text(`up to ${state.maximum} ${currency}`, `до ${state.maximum} ${currency}`));
    if (Number(state?.page || 1) > 1) parts.push(text(`page ${state.page}`, `страница ${state.page}`));
    return parts.length ? parts.join(' · ') : text('All resources', 'Все ресурсы');
  };

  const captureHomeState = () => {
    const home = document.querySelector('.lab-home');
    if (!home) return null;
    const filters = [...home.querySelectorAll('[data-lab-home-filter].is-active')].map((button) => ({
      group: button.dataset.filterGroup || '',
      value: button.dataset.filterValue || '',
      label: button.querySelector('strong')?.textContent?.trim() || button.dataset.filterValue || ''
    }));
    const url = new URL(window.location.href);
    url.searchParams.delete('lab_resume');
    return {
      url: `${url.pathname}${url.search}`,
      filters,
      minimum: home.querySelector('[data-lab-home-price-min]')?.value || '',
      maximum: home.querySelector('[data-lab-home-price-max]')?.value || '',
      sort: home.querySelector('[data-lab-home-sort]')?.value || 'manual',
      page: Number(url.searchParams.get('page') || 1),
      scrollY: Math.round(window.scrollY),
      savedAt: Date.now()
    };
  };

  let homeStateTimer;
  let catalogWasUsed = false;
  const saveHomeState = () => {
    const state = captureHomeState();
    if (!state) return;
    writeJson(window.localStorage, KEYS.catalogResume, state);
    renderResumeStrip(state);
  };

  const scheduleHomeStateSave = (delay = 220) => {
    window.clearTimeout(homeStateTimer);
    homeStateTimer = window.setTimeout(saveHomeState, delay);
  };

  const renderResumeStrip = (providedState) => {
    const state = providedState || readJson(window.localStorage, KEYS.catalogResume, null);
    document.querySelectorAll('[data-lab-resume-strip]').forEach((strip) => {
      if (!state?.savedAt || Date.now() - state.savedAt < 45000) {
        strip.hidden = true;
        return;
      }
      const summary = strip.querySelector('[data-lab-resume-summary]');
      const title = strip.querySelector('[data-lab-resume-title]');
      if (summary) summary.textContent = summarizeHomeState(state);
      if (title) title.textContent = text('Continue browsing', 'Продолжить просмотр');
      strip.hidden = false;
    });
  };

  const applyStoredHomeState = (state) => {
    const home = document.querySelector('.lab-home');
    if (!home || !state) return;
    const wanted = new Set((state.filters || []).map((filter) => `${filter.group}::${normalize(filter.value)}`));
    home.querySelectorAll('[data-lab-home-filter]').forEach((button) => {
      const key = `${button.dataset.filterGroup || ''}::${normalize(button.dataset.filterValue)}`;
      const shouldBeActive = wanted.has(key);
      if (button.classList.contains('is-active') !== shouldBeActive) button.click();
    });

    const minimum = home.querySelector('[data-lab-home-price-min]');
    const maximum = home.querySelector('[data-lab-home-price-max]');
    if (minimum) {
      minimum.value = state.minimum || '';
      minimum.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (maximum) {
      maximum.value = state.maximum || '';
      maximum.dispatchEvent(new Event('input', { bubbles: true }));
    }
    const sort = home.querySelector('[data-lab-home-sort]');
    if (sort && state.sort) {
      sort.value = state.sort;
      sort.dispatchEvent(new Event('change', { bubbles: true }));
    }
    window.setTimeout(() => window.scrollTo({ top: Number(state.scrollY || 0), behavior: 'smooth' }), 320);
    document.querySelectorAll('[data-lab-resume-strip]').forEach((strip) => { strip.hidden = true; });
    publish('catalog_session_resumed', { summary: summarizeHomeState(state) });
  };

  const resumeHomeState = () => {
    const state = readJson(window.localStorage, KEYS.catalogResume, null);
    if (!state) return;
    const target = new URL(state.url || rootUrl, window.location.origin);
    const current = new URL(window.location.href);
    const targetPage = target.searchParams.get('page') || '1';
    const currentPage = current.searchParams.get('page') || '1';
    if (target.pathname !== current.pathname || targetPage !== currentPage) {
      target.searchParams.set('lab_resume', '1');
      window.location.assign(target.toString());
      return;
    }
    applyStoredHomeState(state);
  };

  const getSavedSearches = () => {
    const value = readJson(window.localStorage, KEYS.savedSearches, []);
    return Array.isArray(value) ? value : [];
  };

  const searchDefinitionKey = (search) => JSON.stringify({
    kind: search.kind,
    query: normalize(search.query),
    filters: (search.filters || []).map((filter) => [filter.group, normalize(filter.value)]).sort(),
    minimum: search.minimum || '',
    maximum: search.maximum || ''
  });

  const saveSearchDefinition = (definition, button) => {
    if (!definition) return;
    const searches = getSavedSearches();
    const key = searchDefinitionKey(definition);
    const existing = searches.find((search) => searchDefinitionKey(search) === key);
    if (existing) {
      existing.lastSeenAt = Date.now();
      existing.url = definition.url;
      existing.label = definition.label;
    } else {
      searches.unshift({
        ...definition,
        id: `search-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        createdAt: Date.now(),
        lastSeenAt: Date.now()
      });
    }
    writeJson(window.localStorage, KEYS.savedSearches, searches.slice(0, 20));
    if (button) {
      const previous = button.textContent;
      button.textContent = text('Saved', 'Сохранено');
      window.setTimeout(() => { button.textContent = previous; }, 1800);
    }
    showToast(text('Search saved in My Lab.', 'Поиск сохранён в My Lab.'));
    renderSavedSearches(latestCatalog);
    updateMyLabRetentionStats();
    publish('search_saved', { kind: definition.kind, label: definition.label });
  };

  const createHomeSearchDefinition = () => {
    const state = captureHomeState();
    if (!state) return null;
    const label = summarizeHomeState(state);
    return {
      kind: 'catalog',
      label,
      url: state.url,
      filters: state.filters,
      minimum: state.minimum,
      maximum: state.maximum,
      sort: state.sort
    };
  };

  const createTextSearchDefinition = (button) => {
    const query = String(button?.dataset.labSearchQuery || document.querySelector('[data-lab-search-page]')?.dataset.labSearchQuery || '').trim();
    if (!query) return null;
    const url = new URL(`${rootUrl}search`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    return {
      kind: 'search',
      label: query,
      query,
      url: `${url.pathname}${url.search}`,
      filters: [],
      minimum: '',
      maximum: '',
      sort: 'relevance'
    };
  };

  const productMatchesSavedSearch = (product, search) => {
    if (search.query) {
      const queryTokens = canonicalSearch(search.query).split(' ').filter((token) => token.length > 1);
      if (!queryTokens.every((token) => product.searchBlob.includes(token))) return false;
    }
    const groups = new Map();
    (search.filters || []).forEach((filter) => {
      if (filter.group === 'collection') return;
      const values = groups.get(filter.group) || [];
      values.push(normalizeType(filter.value));
      groups.set(filter.group, values);
    });
    const matchesGroups = [...groups.entries()].every(([group, values]) => values.some((value) => {
      if (group === 'framework') return product.frameworks.includes(value);
      if (group === 'availability') return product.availability === value;
      return product.searchBlob.includes(value);
    }));
    if (!matchesGroups) return false;
    const minimum = search.minimum === '' ? null : Number(search.minimum) * 100;
    const maximum = search.maximum === '' ? null : Number(search.maximum) * 100;
    if (minimum !== null && Number.isFinite(minimum) && product.priceCents < minimum) return false;
    if (maximum !== null && Number.isFinite(maximum) && product.priceCents > maximum) return false;
    return true;
  };

  const productsForSavedSearch = async (search, fallbackProducts) => {
    const collectionHandles = [...new Set((search.filters || [])
      .filter((filter) => filter.group === 'collection' && normalize(filter.value) !== 'all')
      .map((filter) => String(filter.value).trim())
      .filter(Boolean))];
    if (!collectionHandles.length) return fallbackProducts;
    const batches = await Promise.all(collectionHandles.map((handle) => {
      const url = `${rootUrl}collections/${encodeURIComponent(handle)}/products.json?limit=250`;
      return fetchCatalog(url, 8);
    }));
    return [...new Map(batches.flat().map((product) => [product.handle, product])).values()];
  };

  const renderSavedSearches = async (products = latestCatalog) => {
    const targets = document.querySelectorAll('[data-lab-saved-searches]');
    if (!targets.length) return;
    const searches = getSavedSearches();
    if (!searches.length) {
      targets.forEach((target) => {
        target.innerHTML = `<p class="lab-my-empty">${text('Use Save search above the homepage catalog or search results.', 'Нажмите «Сохранить поиск» над каталогом или результатами поиска.')}</p>`;
      });
      return;
    }

    const rows = await Promise.all(searches.map(async (search) => {
      const sourceProducts = await productsForSavedSearch(search, products);
      const matches = sourceProducts.filter((product) => productMatchesSavedSearch(product, search));
      const newCount = matches.filter((product) => product.publishedMs > Number(search.lastSeenAt || search.createdAt || 0)).length;
      const row = document.createElement('article');
      row.className = 'lab-saved-search';
      const copy = document.createElement('div');
      const heading = document.createElement('h3');
      heading.textContent = search.label || text('Saved search', 'Сохранённый поиск');
      const meta = document.createElement('p');
      meta.textContent = text(`${matches.length} matching resources`, `Совпадений: ${matches.length}`);
      copy.append(heading, meta);
      if (newCount > 0) {
        const badge = document.createElement('strong');
        badge.className = 'lab-saved-search__new';
        badge.textContent = text(`${newCount} new`, `Новых: ${newCount}`);
        copy.append(badge);
      }
      const actions = document.createElement('div');
      const open = document.createElement('a');
      const safeUrl = new URL(search.url || rootUrl, window.location.origin);
      if (search.kind === 'catalog') safeUrl.searchParams.set('lab_saved_search', search.id);
      open.href = safeUrl.origin === window.location.origin ? safeUrl.toString() : rootUrl;
      open.dataset.labOpenSavedSearch = search.id;
      open.textContent = text('Open search', 'Открыть поиск');
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.labDeleteSavedSearch = search.id;
      remove.textContent = text('Delete', 'Удалить');
      actions.append(open, remove);
      row.append(copy, actions);
      return row;
    }));
    targets.forEach((target) => target.replaceChildren(...rows.map((row) => row.cloneNode(true))));
  };

  const initCatalogSession = () => {
    const home = document.querySelector('.lab-home');
    if (!home) return;
    const url = new URL(window.location.href);
    const isResume = url.searchParams.get('lab_resume') === '1';
    const savedSearchId = url.searchParams.get('lab_saved_search');
    if (savedSearchId) {
      const searches = getSavedSearches();
      const search = searches.find((item) => item.id === savedSearchId);
      url.searchParams.delete('lab_saved_search');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      if (search) {
        search.lastSeenAt = Date.now();
        writeJson(window.localStorage, KEYS.savedSearches, searches);
        window.setTimeout(() => applyStoredHomeState({ ...search, scrollY: 0 }), 180);
      }
    } else if (isResume) {
      url.searchParams.delete('lab_resume');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      window.setTimeout(() => applyStoredHomeState(readJson(window.localStorage, KEYS.catalogResume, null)), 180);
    } else {
      renderResumeStrip();
    }

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-lab-resume-catalog]')) {
        resumeHomeState();
        return;
      }
      if (event.target.closest('[data-lab-dismiss-resume]')) {
        removeStored(window.localStorage, KEYS.catalogResume);
        document.querySelectorAll('[data-lab-resume-strip]').forEach((strip) => { strip.hidden = true; });
        publish('catalog_session_dismissed');
        return;
      }
      if (event.target.closest('[data-lab-home-filter], [data-lab-home-pagination] a, [data-lab-home-filter-reset]')) {
        catalogWasUsed = true;
        scheduleHomeStateSave(event.target.closest('[data-lab-home-pagination]') ? 850 : 180);
      }
    });

    document.addEventListener('input', (event) => {
      if (!event.target.closest('[data-lab-home-price-form]')) return;
      catalogWasUsed = true;
      scheduleHomeStateSave(260);
    });
    document.addEventListener('change', (event) => {
      if (!event.target.closest('[data-lab-home-sort], [data-lab-home-mobile-sort]')) return;
      catalogWasUsed = true;
      scheduleHomeStateSave();
    });
    window.addEventListener('scroll', () => {
      if (!catalogWasUsed && !readJson(window.localStorage, KEYS.catalogResume, null)) return;
      scheduleHomeStateSave(600);
    }, { passive: true });
    window.addEventListener('pagehide', () => {
      if (catalogWasUsed || readJson(window.localStorage, KEYS.catalogResume, null)) saveHomeState();
    });
  };

  const BUILD_SLOTS = [
    { id: 'hud', label: 'HUD', terms: ['hud', 'interface'] },
    { id: 'inventory', label: 'Inventory', terms: ['inventory'] },
    { id: 'garage', label: 'Garage', terms: ['garage', 'parking'] },
    { id: 'jobs', label: 'Jobs', terms: ['job', 'jobs', 'business'] },
    { id: 'emergency', label: 'Police / EMS', terms: ['police', 'sheriff', 'lspd', 'ems', 'ambulance'] },
    { id: 'housing', label: 'Housing', terms: ['housing', 'house', 'apartment', 'mansion'] },
    { id: 'vehicles', label: 'Vehicles', terms: ['vehicle', 'vehicles', 'car', 'cars'] },
    { id: 'maps', label: 'Maps / MLOs', terms: ['map', 'maps', 'mlo', 'interior'] },
    { id: 'clothing', label: 'Clothing', terms: ['clothing', 'clothes', 'eup', 'uniform'] },
    { id: 'phone', label: 'Phone', terms: ['phone', 'tablet', 'mobile'] }
  ];

  const starterTerms = {
    rp: ['hud', 'inventory', 'garage', 'housing', 'phone', 'jobs'],
    police: ['police', 'sheriff', 'ems', 'dispatch', 'garage', 'radio'],
    gang: ['gang', 'territory', 'drug', 'weapon', 'inventory', 'housing'],
    racing: ['racing', 'vehicle', 'garage', 'handling', 'track', 'hud']
  };

  const defaultBuild = () => ({
    profile: {
      name: '',
      archetype: 'rp',
      framework: '',
      language: isRussian ? 'ru' : 'en',
      budget: ''
    },
    items: []
  });

  const getBuild = () => {
    const stored = readJson(window.localStorage, KEYS.serverBuild, null);
    const fallback = defaultBuild();
    if (!stored || typeof stored !== 'object') return fallback;
    return {
      profile: { ...fallback.profile, ...(stored.profile || {}) },
      items: Array.isArray(stored.items) ? stored.items.map(normalizeProduct).filter((item) => item.handle).slice(0, 60) : []
    };
  };

  let serverBuild = getBuild();
  const saveBuild = () => {
    writeJson(window.localStorage, KEYS.serverBuild, serverBuild);
    publish('server_build_updated', { itemCount: serverBuild.items.length, framework: serverBuild.profile.framework || '' });
  };

  const resolveProduct = (handle, sourceElement) => {
    if (!handle) return null;
    if (viewedProduct?.handle === handle) return viewedProduct;
    const card = sourceElement?.closest?.('[data-lab-resource-card]')
      || [...document.querySelectorAll('[data-lab-resource-card]')].find((item) => item.dataset.labHandle === handle);
    const fromCatalog = latestCatalog.find((product) => product.handle === handle);
    if (card) {
      const fromCard = productFromCard(card);
      return fromCatalog ? normalizeProduct({
        ...fromCatalog,
        ...fromCard,
        imageCount: fromCatalog.imageCount,
        hasVideo: fromCatalog.hasVideo,
        publishedAt: fromCatalog.publishedAt || fromCard.publishedAt,
        updatedAt: fromCatalog.updatedAt || fromCard.updatedAt
      }) : fromCard;
    }
    return fromCatalog || (productCache[handle] ? normalizeProduct(productCache[handle]) : null);
  };

  const productSlots = (product) => {
    const source = product.searchBlob || normalizeProduct(product).searchBlob;
    return BUILD_SLOTS.filter((slot) => slot.terms.some((term) => source.includes(normalize(term))));
  };

  const isFrameworkCompatible = (product, selectedFramework = serverBuild.profile.framework) => {
    const selected = normalizeType(selectedFramework);
    if (!selected) return true;
    const frameworks = product.frameworks || detectFrameworks(product);
    if (!frameworks.length) return null;
    return frameworks.includes(selected) || frameworks.includes('standalone');
  };

  const buildWarningsFor = (product, items = serverBuild.items) => {
    const warnings = [];
    const frameworkCompatibility = isFrameworkCompatible(product);
    if (frameworkCompatibility === false) {
      warnings.push(text(
        `Framework mismatch: selected ${serverBuild.profile.framework.toUpperCase()}`,
        `Другой фреймворк: выбран ${serverBuild.profile.framework.toUpperCase()}`
      ));
    } else if (frameworkCompatibility === null && serverBuild.profile.framework) {
      warnings.push(text('Framework is not specified', 'Фреймворк не указан'));
    }
    const slots = productSlots(product);
    const duplicateSlots = slots.filter((slot) => items.some((item) => item.handle !== product.handle && productSlots(item).some((itemSlot) => itemSlot.id === slot.id)));
    if (duplicateSlots.length) {
      warnings.push(text(
        `Overlaps: ${duplicateSlots.map((slot) => slot.label).join(', ')}`,
        `Дублирует: ${duplicateSlots.map((slot) => slot.label).join(', ')}`
      ));
    }
    const dependencyNames = Array.isArray(product.dependencies)
      ? product.dependencies
      : String(product.dependencies || '').split(',');
    const buildBlob = normalize(items.map((item) => `${item.handle} ${item.title} ${item.tags.join(' ')}`).join(' '));
    const missingDependencies = dependencyNames.map(String).map((item) => item.trim()).filter(Boolean)
      .filter((dependency) => !buildBlob.includes(normalize(dependency)));
    if (missingDependencies.length) {
      warnings.push(text(
        `Missing dependencies: ${missingDependencies.join(', ')}`,
        `Не хватает зависимостей: ${missingDependencies.join(', ')}`
      ));
    }
    const incompatibleNames = Array.isArray(product.incompatibleWith)
      ? product.incompatibleWith
      : String(product.incompatibleWith || '').split(',');
    const conflicts = incompatibleNames.map(String).map((item) => item.trim()).filter(Boolean)
      .filter((name) => buildBlob.includes(normalize(name)));
    if (conflicts.length) {
      warnings.push(text(
        `Conflicts with: ${conflicts.join(', ')}`,
        `Конфликтует с: ${conflicts.join(', ')}`
      ));
    }
    return warnings;
  };

  const syncBuildButtons = (scope = document) => {
    const handles = new Set(serverBuild.items.map((item) => item.handle));
    scope.querySelectorAll('[data-lab-build-add]').forEach((button) => {
      const active = handles.has(button.dataset.labBuildAdd);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const label = button.querySelector('[data-lab-build-label]');
      if (label) label.textContent = active ? text('In server build', 'В сборке') : text('Add to server build', 'В сборку сервера');
      if (!label && button.matches('.lab-retention-product__actions button')) {
        button.textContent = active ? text('In build', 'В сборке') : text('Add to build', 'В сборку');
      }
      button.title = active ? text('Remove from server build', 'Удалить из сборки') : text('Add to server build', 'Добавить в сборку');
    });
  };

  const buildFitFor = (product) => {
    const selectedFramework = normalizeType(serverBuild.profile.framework);
    const hasProfile = Boolean(
      selectedFramework
      || serverBuild.profile.name
      || serverBuild.profile.budget
      || serverBuild.items.length
    );
    if (!hasProfile) return null;

    const normalizedProduct = normalizeProduct(product);
    const compatible = isFrameworkCompatible(normalizedProduct, selectedFramework);
    const coveredSlotIds = new Set(serverBuild.items.flatMap((item) => productSlots(item).map((slot) => slot.id)));
    const slots = productSlots(normalizedProduct);
    const missingSlots = slots.filter((slot) => !coveredSlotIds.has(slot.id));
    const archetypeTerms = starterTerms[serverBuild.profile.archetype] || starterTerms.rp;
    const archetypeMatch = archetypeTerms.some((term) => normalizedProduct.searchBlob.includes(normalize(term)));
    const selectedLanguage = normalize(serverBuild.profile.language);
    const languageKnown = normalizedProduct.languages.length > 0;
    const languageMatch = !languageKnown
      || selectedLanguage === 'multi'
      || normalizedProduct.languages.some((language) => {
        const candidate = normalize(language);
        return candidate === selectedLanguage || candidate === 'multi' || candidate === 'multilingual';
      });
    const totalCents = serverBuild.items.reduce((sum, item) => sum + Number(item.priceCents || 0), 0);
    const budgetCents = Number(serverBuild.profile.budget || 0) * 100;
    const remainingCents = budgetCents ? budgetCents - totalCents : 0;
    const withinBudget = !budgetCents || normalizedProduct.priceCents <= Math.max(0, remainingCents);
    const warnings = buildWarningsFor(normalizedProduct);

    let score = 0;
    const reasons = [];
    if (!selectedFramework) {
      score += 24;
      reasons.push(text('Choose a framework for a precise check', 'Выберите фреймворк для точной проверки'));
    } else if (compatible === true) {
      score += 48;
      reasons.push(text(`${serverBuild.profile.framework.toUpperCase()} compatible`, `Подходит для ${serverBuild.profile.framework.toUpperCase()}`));
    } else if (compatible === null) {
      score += 22;
      reasons.push(text('Framework is not specified', 'Фреймворк не указан'));
    } else {
      reasons.push(text('Framework mismatch', 'Другой фреймворк'));
    }

    if (missingSlots.length) {
      score += Math.min(27, 17 + (missingSlots.length * 5));
      reasons.push(text(`Fills ${missingSlots[0].label}`, `Закрывает ${missingSlots[0].label}`));
    } else if (slots.length) {
      score += 7;
      reasons.push(text(`${slots[0].label} already covered`, `${slots[0].label} уже закрыт`));
    }

    if (archetypeMatch) {
      score += 15;
      reasons.push(text(`Matches ${serverBuild.profile.archetype.toUpperCase()} style`, `Подходит под стиль ${serverBuild.profile.archetype.toUpperCase()}`));
    }
    if (languageMatch) {
      score += languageKnown ? 6 : 3;
      if (languageKnown) reasons.push(text('Language match', 'Подходящий язык'));
    }
    if (budgetCents) {
      score += withinBudget ? 8 : -12;
      reasons.push(withinBudget ? text('Within remaining budget', 'В пределах бюджета') : text('Over remaining budget', 'Выше остатка бюджета'));
    }
    score -= Math.min(24, warnings.length * 8);
    score = Math.max(0, Math.min(100, score));
    if (compatible === false) score = Math.min(score, 24);

    const tone = compatible === false || warnings.length > 0
      ? 'warning'
      : compatible === null || !selectedFramework
        ? 'neutral'
        : score >= 78
          ? 'strong'
          : 'good';
    const label = tone === 'strong'
      ? text('strong build fit', 'сильное совпадение')
      : tone === 'warning'
        ? text('review before adding', 'нужна проверка')
        : tone === 'neutral'
          ? text('partial build data', 'неполные данные')
          : text('fits your build', 'подходит сборке');

    return { score, tone, label, reasons, warnings, compatible, missingSlots, withinBudget };
  };

  const updateCompatibility = (scope = document) => {
    const cards = [];
    if (scope.matches?.('[data-lab-resource-card]')) cards.push(scope);
    scope.querySelectorAll?.('[data-lab-resource-card]').forEach((card) => cards.push(card));
    cards.forEach((card) => {
      const target = card.querySelector('[data-lab-card-match]');
      if (!target) return;
      const product = productFromCard(card);
      const fit = buildFitFor(product);
      if (!fit) {
        target.hidden = true;
        return;
      }
      target.replaceChildren();
      const score = document.createElement('strong');
      score.textContent = `${fit.score}%`;
      target.append(score, document.createTextNode(fit.label));
      target.title = [...fit.reasons, ...fit.warnings].join(' · ');
      target.classList.toggle('is-warning', fit.tone === 'warning');
      target.classList.toggle('is-neutral', fit.tone === 'neutral');
      target.classList.toggle('is-strong', fit.tone === 'strong');
      target.hidden = false;
    });

    document.querySelectorAll('[data-lab-product-compatibility]').forEach((target) => {
      const fit = viewedProduct ? buildFitFor(viewedProduct) : null;
      if (!viewedProduct || !fit) {
        target.hidden = true;
        return;
      }
      target.classList.toggle('is-warning', fit.tone === 'warning');
      target.classList.toggle('is-neutral', fit.tone === 'neutral');
      target.classList.toggle('is-strong', fit.tone === 'strong');
      target.replaceChildren();

      const score = document.createElement('span');
      score.className = 'lab-product-compatibility__score';
      score.textContent = `${fit.score}%`;

      const copy = document.createElement('div');
      copy.className = 'lab-product-compatibility__copy';
      const strong = document.createElement('strong');
      strong.textContent = fit.label.charAt(0).toUpperCase() + fit.label.slice(1);
      const description = document.createElement('small');
      description.textContent = text('Calculated from your build profile and this listing metadata.', 'Рассчитано по профилю сборки и данным этого товара.');
      copy.append(strong, description);
      target.append(score, copy);

      const reasons = document.createElement('div');
      reasons.className = 'lab-product-compatibility__reasons';
      [...fit.reasons, ...fit.warnings].slice(0, 4).forEach((reason) => {
        const chip = document.createElement('span');
        chip.textContent = reason;
        reasons.append(chip);
      });
      target.append(reasons);

      if (fit.compatible === false && serverBuild.profile.framework) {
        const link = document.createElement('a');
        link.href = `${rootUrl}search?type=product&q=${encodeURIComponent(`${viewedProduct.type} ${serverBuild.profile.framework}`)}`;
        link.textContent = text('Find a compatible alternative', 'Найти совместимую альтернативу');
        target.append(link);
      }
      target.hidden = false;
    });
  };

  const createBuildItem = (product) => {
    const item = document.createElement('article');
    item.className = 'lab-build-item';
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = '';
      image.loading = 'lazy';
      item.append(image);
    }
    const copy = document.createElement('div');
    const heading = document.createElement('h4');
    const link = document.createElement('a');
    link.href = product.url;
    link.textContent = product.title;
    heading.append(link);
    const meta = document.createElement('p');
    meta.textContent = [product.vendor, formatPrice(product)].filter(Boolean).join(' · ');
    copy.append(heading, meta);
    const warnings = buildWarningsFor(product);
    if (warnings.length) {
      const warning = document.createElement('small');
      warning.className = 'lab-build-item__warning';
      warning.textContent = warnings.join(' · ');
      copy.append(warning);
    }
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.dataset.labRemoveBuild = product.handle;
    remove.setAttribute('aria-label', text(`Remove ${product.title}`, `Удалить ${product.title}`));
    remove.textContent = '×';
    item.append(copy, remove);
    return item;
  };

  const renderBuild = () => {
    document.querySelectorAll('[data-lab-server-profile]').forEach((form) => {
      if (form.dataset.labProfileHydrated === 'true') return;
      Object.entries(serverBuild.profile).forEach(([name, value]) => {
        const field = form.elements.namedItem(name);
        if (field) field.value = value ?? '';
      });
      form.dataset.labProfileHydrated = 'true';
    });

    const slotMap = new Map(BUILD_SLOTS.map((slot) => [slot.id, []]));
    serverBuild.items.forEach((product) => {
      productSlots(product).forEach((slot) => slotMap.get(slot.id)?.push(product));
    });
    const completeSlots = BUILD_SLOTS.filter((slot) => slotMap.get(slot.id)?.length);
    const missingSlots = BUILD_SLOTS.filter((slot) => !slotMap.get(slot.id)?.length);
    const readiness = Math.round((completeSlots.length / BUILD_SLOTS.length) * 100);
    const totalCents = serverBuild.items.reduce((sum, product) => sum + Number(product.priceCents || 0), 0);
    const budgetCents = Number(serverBuild.profile.budget || 0) * 100;
    const allWarnings = serverBuild.items.flatMap((product) => buildWarningsFor(product));
    const conflictCount = allWarnings.filter((warning) => /mismatch|другой фреймворк|overlaps|дублирует|conflicts with|конфликтует/i.test(warning)).length;
    const dependencyCount = allWarnings.filter((warning) => /missing dependencies|не хватает зависимостей/i.test(warning)).length;
    const hasProfile = Boolean(serverBuild.profile.name || serverBuild.profile.framework || serverBuild.profile.budget || serverBuild.items.length);

    document.querySelectorAll('[data-lab-build-readiness]').forEach((target) => { target.textContent = `${readiness}%`; });
    document.querySelectorAll('[data-lab-build-progress]').forEach((target) => { target.style.width = `${readiness}%`; });
    document.querySelectorAll('[data-lab-build-ring]').forEach((target) => { target.style.setProperty('--lab-build-progress', `${readiness * 3.6}deg`); });
    document.querySelectorAll('[data-lab-header-build-readiness]').forEach((target) => {
      target.textContent = `${readiness}%`;
      target.hidden = !hasProfile;
    });
    document.querySelectorAll('[data-lab-build-dock-project]').forEach((target) => {
      target.textContent = serverBuild.profile.name || text('Untitled build', 'Сборка без названия');
    });
    document.querySelectorAll('[data-lab-build-dock-count]').forEach((target) => { target.textContent = String(serverBuild.items.length); });
    document.querySelectorAll('[data-lab-build-dock-missing]').forEach((target) => {
      target.textContent = text(`${missingSlots.length} missing`, `Не хватает: ${missingSlots.length}`);
    });
    document.querySelectorAll('[data-lab-build-dock-conflicts]').forEach((target) => { target.textContent = String(conflictCount); });
    document.querySelectorAll('[data-lab-build-dock-dependencies]').forEach((target) => { target.textContent = String(dependencyCount); });
    document.querySelectorAll('[data-lab-build-dock-compare]').forEach((target) => {
      const compared = readJson(window.localStorage, KEYS.compare, []);
      target.textContent = String(Array.isArray(compared) ? compared.length : 0);
    });
    document.querySelectorAll('[data-lab-build-health]').forEach((target) => {
      target.textContent = conflictCount || dependencyCount
        ? text('Build needs review', 'Сборку нужно проверить')
        : readiness === 100
          ? text('Core stack complete', 'Основная сборка готова')
          : serverBuild.profile.framework
            ? text(`${serverBuild.profile.framework.toUpperCase()} build in progress`, `Сборка ${serverBuild.profile.framework.toUpperCase()} в работе`)
            : text('Choose your framework', 'Выберите фреймворк');
    });
    document.querySelectorAll('[data-lab-build-readiness-copy]').forEach((target) => {
      target.textContent = text(
        `${completeSlots.length} of ${BUILD_SLOTS.length} essential systems covered`,
        `Заполнено основных систем: ${completeSlots.length} из ${BUILD_SLOTS.length}`
      );
    });
    document.querySelectorAll('[data-lab-build-total]').forEach((target) => { target.textContent = moneyFormatter.format(totalCents / 100); });
    document.querySelectorAll('[data-lab-build-budget-status]').forEach((target) => {
      if (!budgetCents) {
        target.textContent = text('Set a budget in the profile', 'Укажите бюджет в профиле');
        target.classList.remove('is-over');
      } else {
        const remaining = budgetCents - totalCents;
        target.textContent = remaining >= 0
          ? text(`${moneyFormatter.format(remaining / 100)} left`, `Осталось ${moneyFormatter.format(remaining / 100)}`)
          : text(`${moneyFormatter.format(Math.abs(remaining) / 100)} over budget`, `Превышение на ${moneyFormatter.format(Math.abs(remaining) / 100)}`);
        target.classList.toggle('is-over', remaining < 0);
      }
    });

    document.querySelectorAll('[data-lab-build-next-steps]').forEach((target) => {
      if (!missingSlots.length) {
        target.innerHTML = `<p class="lab-my-empty">${text('All essential systems are covered.', 'Все основные системы закрыты.')}</p>`;
        return;
      }
      target.replaceChildren(...missingSlots.slice(0, 3).map((slot) => {
        const link = document.createElement('a');
        link.href = `${rootUrl}search?type=product&q=${encodeURIComponent(`${slot.terms[0]} ${serverBuild.profile.framework || ''}`.trim())}`;
        const label = document.createElement('strong');
        label.textContent = slot.label;
        const action = document.createElement('span');
        action.textContent = text('Find a match →', 'Найти вариант →');
        link.append(label, action);
        return link;
      }));
    });

    document.querySelectorAll('[data-lab-build-slots]').forEach((target) => {
      target.replaceChildren(...BUILD_SLOTS.map((slot) => {
        const matches = slotMap.get(slot.id) || [];
        const row = document.createElement('div');
        row.className = `lab-build-slot${matches.length ? ' is-complete' : ''}`;
        const status = document.createElement('span');
        status.setAttribute('aria-hidden', 'true');
        status.textContent = matches.length ? '✓' : '+';
        const copy = document.createElement('div');
        const strong = document.createElement('strong');
        strong.textContent = slot.label;
        const small = document.createElement('small');
        small.textContent = matches.length
          ? text(`${matches.length} selected`, `Выбрано: ${matches.length}`)
          : text('Missing from this build', 'Не добавлено');
        copy.append(strong, small);
        const find = document.createElement('a');
        find.href = `${rootUrl}search?type=product&q=${encodeURIComponent(`${slot.terms[0]} ${serverBuild.profile.framework || ''}`.trim())}`;
        find.textContent = matches.length ? text('Review', 'Проверить') : text('Find', 'Найти');
        row.append(status, copy, find);
        return row;
      }));
    });

    document.querySelectorAll('[data-lab-build-items]').forEach((target) => {
      if (!serverBuild.items.length) {
        target.innerHTML = `<p class="lab-my-empty">${text('Add resources from cards or product pages.', 'Добавляйте ресурсы из карточек или со страниц товаров.')}</p>`;
      } else {
        target.replaceChildren(...serverBuild.items.map(createBuildItem));
      }
    });
    document.querySelectorAll('[data-lab-build-count-copy]').forEach((target) => {
      target.textContent = text(`${serverBuild.items.length} resources selected.`, `Выбрано ресурсов: ${serverBuild.items.length}.`);
    });

    document.querySelectorAll('[data-lab-build-recommendations]').forEach((section) => {
      const target = section.querySelector('[data-lab-build-recommendation-target]');
      const missing = missingSlots;
      const selected = new Set(serverBuild.items.map((product) => product.handle));
      const suggestions = latestCatalog
        .filter((product) => !selected.has(product.handle) && isFrameworkCompatible(product))
        .map((product) => ({
          product,
          score: missing.reduce((sum, slot) => sum + (slot.terms.some((term) => product.searchBlob.includes(normalize(term))) ? 25 : 0), 0)
            + interestScore(product)
            + ((starterTerms[serverBuild.profile.archetype] || []).some((term) => product.searchBlob.includes(normalize(term))) ? 18 : 0)
            + (serverBuild.profile.language && product.searchBlob.includes(normalize(serverBuild.profile.language)) ? 6 : 0)
            + (Number(serverBuild.profile.budget || 0) > 0 && product.priceCents <= Number(serverBuild.profile.budget) * 100 ? 5 : 0)
        }))
        .filter((entry) => entry.score > 0)
        .sort((left, right) => right.score - left.score || right.product.publishedMs - left.product.publishedMs)
        .slice(0, 8)
        .map((entry) => entry.product);
      if (!target || !suggestions.length || !missing.length) {
        section.hidden = true;
        return;
      }
      target.replaceChildren(...suggestions.map((product) => createProductTile(product)));
      section.hidden = false;
    });

    syncBuildButtons();
    updateCompatibility();
    updateMyLabRetentionStats();
  };

  const toggleBuildProduct = (handle, sourceElement) => {
    const index = serverBuild.items.findIndex((item) => item.handle === handle);
    if (index >= 0) {
      serverBuild.items.splice(index, 1);
      showToast(text('Removed from server build.', 'Удалено из сборки сервера.'));
    } else {
      const product = resolveProduct(handle, sourceElement);
      if (!product) return;
      serverBuild.items.push(product);
      rememberProducts([product]);
      const warnings = buildWarningsFor(product, serverBuild.items);
      showToast(warnings[0] || text('Added to server build.', 'Добавлено в сборку сервера.'));
    }
    saveBuild();
    renderBuild();
  };

  const applyStarterBuild = (kind) => {
    const terms = starterTerms[kind] || starterTerms.rp;
    const current = new Set(serverBuild.items.map((product) => product.handle));
    const additions = [];
    terms.forEach((term) => {
      const candidate = latestCatalog
        .filter((product) => !current.has(product.handle) && !additions.some((item) => item.handle === product.handle))
        .filter((product) => isFrameworkCompatible(product))
        .filter((product) => product.searchBlob.includes(normalize(term)))
        .sort((left, right) => interestScore(right) - interestScore(left) || right.publishedMs - left.publishedMs)[0];
      if (candidate) additions.push(candidate);
    });
    serverBuild.profile.archetype = kind;
    serverBuild.items.push(...additions);
    saveBuild();
    document.querySelectorAll('[data-lab-server-profile]').forEach((form) => { delete form.dataset.labProfileHydrated; });
    renderBuild();
    showToast(text(`${additions.length} suggestions added.`, `Добавлено предложений: ${additions.length}.`));
  };

  const getWatched = () => {
    const watched = readJson(window.localStorage, KEYS.watched, {});
    return watched && typeof watched === 'object' && !Array.isArray(watched) ? watched : {};
  };

  let watchedResources = getWatched();
  const productSnapshot = (product) => ({
    title: product.title,
    descriptionKey: product.descriptionKey || '',
    version: product.version || '',
    priceCents: Number(product.priceCents || 0),
    updatedMs: Number(product.updatedMs || 0),
    imageCount: Number(product.imageCount || 0),
    hasVideo: Boolean(product.hasVideo),
    externalUrl: product.externalUrl || '',
    lastVerifiedAt: product.lastVerifiedAt || '',
    verificationStatus: product.verificationStatus || '',
    checkedAt: Date.now()
  });

  const snapshotChanges = (previous, current) => {
    if (!previous) return [];
    const changes = [];
    if (Number(previous.priceCents) !== Number(current.priceCents)) {
      changes.push({
        key: `price:${previous.priceCents}:${current.priceCents}`,
        label: text(
          `Price changed from ${moneyFormatter.format(Number(previous.priceCents || 0) / 100)} to ${moneyFormatter.format(Number(current.priceCents || 0) / 100)}`,
          `Цена изменилась: ${moneyFormatter.format(Number(previous.priceCents || 0) / 100)} → ${moneyFormatter.format(Number(current.priceCents || 0) / 100)}`
        )
      });
    }
    if (!previous.hasVideo && current.hasVideo) changes.push({ key: 'video:added', label: text('A video was added', 'Добавлено видео') });
    if (Number(current.imageCount) > Number(previous.imageCount)) changes.push({ key: `images:${current.imageCount}`, label: text('New images were added', 'Добавлены изображения') });
    if (previous.descriptionKey && current.descriptionKey && previous.descriptionKey !== current.descriptionKey) {
      changes.push({ key: `description:${current.updatedMs || current.checkedAt}`, label: text('The description changed', 'Описание товара изменилось') });
    }
    if (previous.title && current.title && previous.title !== current.title) {
      changes.push({ key: `title:${current.title}`, label: text('The resource title changed', 'Название ресурса изменилось') });
    }
    if (previous.version && current.version && previous.version !== current.version) {
      changes.push({ key: `version:${current.version}`, label: text(`Version changed to ${current.version}`, `Версия изменена на ${current.version}`) });
    }
    if (previous.externalUrl && current.externalUrl && previous.externalUrl !== current.externalUrl) {
      changes.push({ key: `link:${current.externalUrl}`, label: text('The creator store link changed', 'Изменилась ссылка на магазин автора') });
    }
    if (previous.verificationStatus && current.verificationStatus && previous.verificationStatus !== current.verificationStatus) {
      changes.push({
        key: `verification:${current.verificationStatus}:${current.lastVerifiedAt}`,
        label: text(`Verification status changed to ${current.verificationStatus}`, `Статус проверки изменён: ${current.verificationStatus}`)
      });
    }
    if (current.updatedMs > Number(previous.updatedMs || 0) && !changes.length) {
      changes.push({ key: `updated:${current.updatedMs}`, label: text('The listing was updated', 'Карточка товара обновлена') });
    }
    return changes;
  };

  const checkWatchedProducts = (products = latestCatalog) => {
    let changed = false;
    Object.entries(watchedResources).forEach(([handle, record]) => {
      const currentProduct = (viewedProduct?.handle === handle ? viewedProduct : null)
        || products.find((product) => product.handle === handle)
        || (productCache[handle] ? normalizeProduct(productCache[handle]) : null);
      if (!currentProduct) return;
      const currentSnapshot = productSnapshot(currentProduct);
      if (!currentProduct.detailSnapshot && record.snapshot) {
        currentSnapshot.hasVideo = Boolean(record.snapshot.hasVideo);
        currentSnapshot.externalUrl = record.snapshot.externalUrl || '';
        currentSnapshot.descriptionKey = record.snapshot.descriptionKey || '';
        currentSnapshot.version = currentSnapshot.version || record.snapshot.version || '';
        currentSnapshot.lastVerifiedAt = currentSnapshot.lastVerifiedAt || record.snapshot.lastVerifiedAt || '';
        currentSnapshot.verificationStatus = currentSnapshot.verificationStatus || record.snapshot.verificationStatus || '';
      }
      const existingChanges = Array.isArray(record.changes) ? record.changes : [];
      const existingKeys = new Set(existingChanges.map((item) => item.key));
      const additions = snapshotChanges(record.snapshot, currentSnapshot)
        .filter((item) => !existingKeys.has(item.key))
        .map((item) => ({ ...item, at: Date.now(), seen: false }));
      watchedResources[handle] = {
        ...record,
        product: currentProduct,
        snapshot: currentSnapshot,
        changes: [...additions, ...existingChanges].slice(0, 30)
      };
      if (additions.length) changed = true;
    });
    writeJson(window.localStorage, KEYS.watched, watchedResources);
    if (changed) publish('tracked_resource_change', { unread: countTrackedChanges() });
  };

  const syncTrackedButtons = (scope = document) => {
    const handles = new Set(Object.keys(watchedResources));
    scope.querySelectorAll('[data-lab-track-resource]').forEach((button) => {
      const active = handles.has(button.dataset.labTrackResource);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const label = button.querySelector('[data-lab-track-label]');
      if (label) label.textContent = active ? text('Tracking changes', 'Изменения отслеживаются') : text('Track changes', 'Отслеживать изменения');
    });
  };

  const toggleTrackedProduct = (handle, sourceElement) => {
    if (watchedResources[handle]) {
      delete watchedResources[handle];
      showToast(text('Change tracking stopped.', 'Отслеживание отключено.'));
      publish('resource_untracked', { handle });
    } else {
      const product = resolveProduct(handle, sourceElement);
      if (!product) return;
      watchedResources[handle] = {
        product,
        snapshot: productSnapshot(product),
        changes: [],
        startedAt: Date.now()
      };
      showToast(text('Changes will be shown in My Lab.', 'Изменения будут появляться в My Lab.'));
      publish('resource_tracked', { handle });
    }
    writeJson(window.localStorage, KEYS.watched, watchedResources);
    renderWatchedResources();
    syncTrackedButtons();
    updateHeaderCount();
    updateMyLabRetentionStats();
  };

  const renderWatchedResources = () => {
    document.querySelectorAll('[data-lab-watched-resources]').forEach((target) => {
      const records = Object.entries(watchedResources);
      if (!records.length) {
        target.innerHTML = `<p class="lab-my-empty">${text('Use Track changes on a resource page to watch its listing.', 'Нажмите «Отслеживать изменения» на странице ресурса.')}</p>`;
        return;
      }
      target.replaceChildren(...records.map(([handle, record]) => {
        const product = normalizeProduct(record.product || productCache[handle] || { handle });
        const item = document.createElement('article');
        item.className = 'lab-watched-resource';
        const tile = createProductTile(product);
        const changes = document.createElement('div');
        changes.className = 'lab-watched-resource__changes';
        const entries = Array.isArray(record.changes) ? record.changes : [];
        if (!entries.length) {
          changes.textContent = text('No changes recorded yet.', 'Изменений пока нет.');
        } else {
          changes.append(...entries.slice(0, 5).map((change) => {
            const row = document.createElement('p');
            row.classList.toggle('is-unread', !change.seen);
            row.textContent = `${change.label} · ${relativeDate(change.at)}`;
            return row;
          }));
        }
        const stop = document.createElement('button');
        stop.type = 'button';
        stop.dataset.labTrackResource = handle;
        stop.textContent = text('Stop tracking', 'Не отслеживать');
        item.append(tile, changes, stop);
        return item;
      }));
    });
    syncTrackedButtons();
  };

  const markTrackedChangesSeen = (removeSeen = false) => {
    Object.values(watchedResources).forEach((record) => {
      const changes = Array.isArray(record.changes) ? record.changes : [];
      record.changes = removeSeen ? changes.filter((change) => !change.seen) : changes.map((change) => ({ ...change, seen: true }));
    });
    writeJson(window.localStorage, KEYS.watched, watchedResources);
    renderWatchedResources();
    updateHeaderCount();
    updateMyLabRetentionStats();
  };

  const getComparedHandles = () => {
    const value = readJson(window.localStorage, KEYS.compare, []);
    return Array.isArray(value) ? value.filter(Boolean).slice(0, 4) : [];
  };

  let comparedHandles = getComparedHandles();
  const syncCompareButtons = (scope = document) => {
    const selected = new Set(comparedHandles);
    scope.querySelectorAll('[data-lab-compare]').forEach((button) => {
      const active = selected.has(button.dataset.labCompare);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const label = button.querySelector('[data-lab-compare-label]');
      if (label) label.textContent = active ? text('Added to comparison', 'Добавлено к сравнению') : text('Compare resource', 'Сравнить ресурс');
    });
  };

  const comparedProducts = () => comparedHandles.map((handle) => resolveProduct(handle)).filter(Boolean);

  const ensureCompareTray = () => {
    let tray = document.querySelector('[data-lab-compare-tray]');
    if (!tray) {
      tray = document.createElement('aside');
      tray.className = 'lab-compare-tray';
      tray.dataset.labCompareTray = '';
      tray.setAttribute('aria-live', 'polite');
      tray.innerHTML = `<span><strong data-lab-compare-count>0</strong> ${text('selected for comparison', 'выбрано для сравнения')}</span><div><button type="button" data-lab-open-compare>${text('Compare', 'Сравнить')}</button><button type="button" data-lab-clear-compare aria-label="${text('Clear comparison', 'Очистить сравнение')}">×</button></div>`;
      document.body.append(tray);
    }
    const count = tray.querySelector('[data-lab-compare-count]');
    if (count) count.textContent = String(comparedHandles.length);
    tray.hidden = comparedHandles.length === 0;
    tray.querySelector('[data-lab-open-compare]')?.toggleAttribute('disabled', comparedHandles.length < 2);
    return tray;
  };

  const toggleComparedProduct = (handle, sourceElement) => {
    const index = comparedHandles.indexOf(handle);
    if (index >= 0) {
      comparedHandles.splice(index, 1);
    } else {
      if (comparedHandles.length >= 4) {
        showToast(text('You can compare up to four resources.', 'Можно сравнить не больше четырёх ресурсов.'));
        return;
      }
      const product = resolveProduct(handle, sourceElement);
      if (!product) return;
      rememberProducts([product]);
      comparedHandles.push(handle);
    }
    writeJson(window.localStorage, KEYS.compare, comparedHandles);
    syncCompareButtons();
    ensureCompareTray();
    renderBuild();
    publish('comparison_updated', { count: comparedHandles.length });
  };

  const comparisonField = (label, products, valueFor) => {
    const row = document.createElement('div');
    row.className = 'lab-compare-row';
    const heading = document.createElement('strong');
    heading.textContent = label;
    row.append(heading);
    products.forEach((product) => {
      const value = document.createElement('span');
      value.textContent = valueFor(product) || '—';
      row.append(value);
    });
    return row;
  };

  const openComparison = () => {
    const products = comparedProducts();
    if (products.length < 2) return;
    document.querySelector('[data-lab-compare-dialog]')?.remove();
    const dialog = document.createElement('dialog');
    dialog.className = 'lab-compare-dialog';
    dialog.dataset.labCompareDialog = '';
    const header = document.createElement('header');
    const title = document.createElement('h2');
    title.textContent = text('Compare resources', 'Сравнение ресурсов');
    const close = document.createElement('button');
    close.type = 'button';
    close.dataset.labCloseCompare = '';
    close.setAttribute('aria-label', text('Close comparison', 'Закрыть сравнение'));
    close.textContent = '×';
    header.append(title, close);

    const table = document.createElement('div');
    table.className = `lab-compare-table lab-compare-table--${products.length}`;
    const productRow = document.createElement('div');
    productRow.className = 'lab-compare-row lab-compare-row--products';
    productRow.append(document.createElement('span'));
    products.forEach((product) => {
      const cell = document.createElement('article');
      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = '';
        cell.append(image);
      }
      const heading = document.createElement('h3');
      const link = document.createElement('a');
      link.href = product.url;
      link.textContent = product.title;
      heading.append(link);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.labCompare = product.handle;
      remove.textContent = text('Remove', 'Убрать');
      cell.append(heading, remove);
      productRow.append(cell);
    });
    table.append(
      productRow,
      comparisonField(text('Price', 'Цена'), products, formatPrice),
      comparisonField(text('Creator', 'Автор'), products, (product) => product.vendor),
      comparisonField(text('Type', 'Категория'), products, (product) => product.type),
      comparisonField(text('Framework', 'Фреймворк'), products, (product) => product.frameworks.map((item) => item.toUpperCase()).join(', ')),
      comparisonField(text('Updated', 'Обновлено'), products, (product) => relativeDate(product.updatedMs || product.publishedMs)),
      comparisonField(text('Your build', 'Ваша сборка'), products, (product) => isFrameworkCompatible(product) ? text('Compatible', 'Подходит') : text('Check compatibility', 'Нужна проверка'))
    );
    const actions = document.createElement('footer');
    products.forEach((product) => {
      const add = document.createElement('button');
      add.type = 'button';
      add.dataset.labBuildAdd = product.handle;
      add.textContent = serverBuild.items.some((item) => item.handle === product.handle)
        ? text('In build', 'В сборке')
        : text('Add to build', 'В сборку');
      actions.append(add);
    });
    dialog.append(header, table, actions);
    document.body.append(dialog);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => dialog.remove(), { once: true });
    dialog.showModal();
    publish('comparison_opened', { count: products.length });
  };

  const initResourceSignals = (scope = document) => {
    const stored = readJson(window.localStorage, KEYS.resourceSignals, {});
    scope.querySelectorAll('[data-lab-resource-feedback]').forEach((panel) => {
      const handle = panel.dataset.labResourceHandle;
      const selected = new Set(Array.isArray(stored?.[handle]) ? stored[handle].map((entry) => entry.signal) : []);
      panel.querySelectorAll('[data-lab-resource-signal]').forEach((button) => {
        const active = selected.has(button.dataset.labResourceSignal);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    });
  };

  const toggleResourceSignal = (button) => {
    const panel = button.closest('[data-lab-resource-feedback]');
    const handle = panel?.dataset.labResourceHandle;
    const signal = button.dataset.labResourceSignal;
    if (!handle || !signal) return;
    const stored = readJson(window.localStorage, KEYS.resourceSignals, {});
    const entries = Array.isArray(stored[handle]) ? stored[handle] : [];
    const index = entries.findIndex((entry) => entry.signal === signal);
    if (index >= 0) entries.splice(index, 1);
    else entries.push({ signal, at: Date.now() });
    stored[handle] = entries;
    writeJson(window.localStorage, KEYS.resourceSignals, stored);
    initResourceSignals(panel);
    const status = panel.querySelector('[data-lab-resource-signal-status]');
    if (status) status.textContent = index >= 0
      ? text('Signal removed.', 'Отметка удалена.')
      : text('Thanks. Your signal was recorded.', 'Спасибо. Отметка сохранена.');
    publish('resource_signal', { handle, signal, selected: index < 0 });
  };

  const SEARCH_ALIASES = new Map([
    ['car', 'vehicle'], ['cars', 'vehicles'], ['auto', 'vehicle'],
    ['veh', 'vehicle'], ['job', 'jobs'], ['qb', 'qbcore'], ['qb core', 'qbcore'],
    ['interior', 'mlo'], ['interiors', 'mlo'], ['house', 'housing'],
    ['clothes', 'clothing'], ['eup', 'clothing'], ['cellphone', 'phone'],
    ['lspd', 'police'], ['sheriff', 'police'], ['medic', 'ems']
  ]);

  const canonicalSearch = (query) => normalize(query).split(' ').map((token) => SEARCH_ALIASES.get(token) || token).join(' ');

  const editDistance = (left, right) => {
    if (left === right) return 0;
    if (!left.length) return right.length;
    if (!right.length) return left.length;
    const row = Array.from({ length: right.length + 1 }, (_, index) => index);
    for (let i = 1; i <= left.length; i += 1) {
      let previous = row[0];
      row[0] = i;
      for (let j = 1; j <= right.length; j += 1) {
        const saved = row[j];
        row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (left[i - 1] === right[j - 1] ? 0 : 1));
        previous = saved;
      }
    }
    return row[right.length];
  };

  const searchCandidates = (query, products = latestCatalog) => {
    const canonical = canonicalSearch(query);
    const tokens = canonical.split(' ').filter((token) => token.length > 1);
    return products.map((product) => {
      const words = product.searchBlob.split(' ').filter((word) => word.length > 1);
      let score = 0;
      tokens.forEach((token) => {
        if (product.searchBlob.includes(token)) score += 40;
        else if (words.some((word) => editDistance(token, word) <= (token.length > 6 ? 2 : 1))) score += 16;
      });
      if (normalize(product.title).startsWith(tokens[0] || '')) score += 20;
      return { product, score };
    }).filter((entry) => entry.score > 0)
      .sort((left, right) => right.score - left.score || right.product.publishedMs - left.product.publishedMs)
      .slice(0, 6)
      .map((entry) => entry.product);
  };

  const renderSearchRecovery = (products = latestCatalog) => {
    document.querySelectorAll('[data-lab-search-recovery]').forEach((recovery) => {
      const target = recovery.querySelector('[data-lab-search-suggestions]');
      const query = document.querySelector('[data-lab-search-page]')?.dataset.labSearchQuery || '';
      if (!target || !query) return;
      const canonical = canonicalSearch(query);
      const suggestions = searchCandidates(query, products);
      const fragment = document.createDocumentFragment();
      if (canonical && canonical !== normalize(query)) {
        const corrected = document.createElement('a');
        corrected.className = 'lab-search-recovery__query';
        corrected.href = `${rootUrl}search?type=product&q=${encodeURIComponent(canonical)}`;
        corrected.textContent = text(`Search for “${canonical}”`, `Искать «${canonical}»`);
        fragment.append(corrected);
      }
      suggestions.forEach((product) => fragment.append(createProductTile(product)));
      if (!suggestions.length) {
        const empty = document.createElement('p');
        empty.textContent = text('No close matches found. Try a category below.', 'Похожих товаров нет. Попробуйте категорию ниже.');
        fragment.append(empty);
      }
      target.replaceChildren(fragment);
    });
  };

  const enhancePredictiveEmptyStates = (products = latestCatalog) => {
    document.querySelectorAll('[data-lab-predictive-search]').forEach((root) => {
      const results = root.querySelector('[data-lab-predictive-results]');
      const input = root.querySelector('input[type="search"]');
      if (!results || !input || results.dataset.labRecoveryObserver === 'true') return;
      results.dataset.labRecoveryObserver = 'true';
      const render = () => {
        const empty = results.querySelector('.lab-predictive-search__empty');
        if (!empty || results.querySelector('[data-lab-predictive-recovery]')) return;
        const matches = searchCandidates(input.value, latestCatalog).slice(0, 3);
        if (!matches.length) return;
        const group = document.createElement('section');
        group.className = 'lab-predictive-group lab-predictive-group--recovery';
        group.dataset.labPredictiveRecovery = '';
        const heading = document.createElement('strong');
        heading.className = 'lab-predictive-group__title';
        heading.textContent = text('Closest catalog matches', 'Похожие ресурсы');
        group.append(heading);
        matches.forEach((product) => {
          const link = document.createElement('a');
          link.className = 'lab-predictive-result';
          link.href = product.url;
          link.textContent = product.title;
          group.append(link);
        });
        empty.after(group);
      };
      new MutationObserver(render).observe(results, { childList: true, subtree: true });
    });
  };

  const base64Encode = (value) => {
    const bytes = new TextEncoder().encode(value);
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const base64Decode = (value) => {
    const padded = String(value || '').replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(String(value || '').length / 4) * 4, '=');
    const binary = window.atob(padded);
    return new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)));
  };

  const getMyLabUrl = () => {
    const link = document.querySelector('.lab-header-my-lab');
    if (link?.href) return new URL(link.href, window.location.origin);
    return new URL(`${rootUrl}pages/my-lab`, window.location.origin);
  };

  const shareBuild = async () => {
    const url = getMyLabUrl();
    const payload = base64Encode(JSON.stringify({
      version: 1,
      profile: serverBuild.profile,
      handles: serverBuild.items.map((product) => product.handle)
    }));
    url.searchParams.set('lab_build', payload);
    try {
      await navigator.clipboard.writeText(url.toString());
      showToast(text('Share link copied.', 'Ссылка на сборку скопирована.'));
    } catch (error) {
      const input = document.createElement('textarea');
      input.value = url.toString();
      input.setAttribute('readonly', '');
      document.body.append(input);
      input.select();
      document.execCommand('copy');
      input.remove();
      showToast(text('Share link copied.', 'Ссылка на сборку скопирована.'));
    }
    publish('server_build_shared', { itemCount: serverBuild.items.length });
  };

  const importSharedBuild = (products = latestCatalog) => {
    const url = new URL(window.location.href);
    const encoded = url.searchParams.get('lab_build');
    if (!encoded) return false;
    try {
      const payload = JSON.parse(base64Decode(encoded));
      const handles = Array.isArray(payload.handles) ? payload.handles : [];
      serverBuild.profile = { ...serverBuild.profile, ...(payload.profile || {}) };
      serverBuild.items = handles.map((handle) => products.find((product) => product.handle === handle)
        || (productCache[handle] ? normalizeProduct(productCache[handle]) : null)).filter(Boolean);
      saveBuild();
      document.querySelectorAll('[data-lab-server-profile]').forEach((form) => { delete form.dataset.labProfileHydrated; });
      url.searchParams.delete('lab_build');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      showToast(text('Shared server build imported.', 'Общая сборка импортирована.'));
      return true;
    } catch (error) {
      console.error(error);
      showToast(text('This build link could not be read.', 'Не удалось прочитать ссылку на сборку.'));
      return false;
    }
  };

  const exportMyLab = () => {
    const data = {
      version: 1,
      exportedAt: new Date().toISOString(),
      savedResources: readJson(window.localStorage, KEYS.saved, []),
      viewedResources: readJson(window.localStorage, 'labViewedResourcesV1', []),
      interests: readJson(window.localStorage, KEYS.interests, {}),
      followedStudios: getFollowedStudios(),
      hiddenStudioUpdates: [...getHiddenStudios()],
      savedSearches: getSavedSearches(),
      serverBuild,
      comparedResources: comparedHandles,
      watchedResources,
      resourceSignals: readJson(window.localStorage, KEYS.resourceSignals, {})
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fivem-lab-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    publish('my_lab_exported');
  };

  const importMyLabFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file || file.size > 2000000) {
        showToast(text('Choose a My Lab JSON file under 2 MB.', 'Выберите JSON My Lab размером до 2 МБ.'));
        return;
      }
      try {
        const data = JSON.parse(await file.text());
        if (Number(data.version) !== 1) throw new Error('Unsupported My Lab export');
        const mappings = [
          [KEYS.saved, data.savedResources],
          ['labViewedResourcesV1', data.viewedResources],
          [KEYS.interests, data.interests],
          [KEYS.followedStudios, data.followedStudios],
          [KEYS.hiddenStudios, data.hiddenStudioUpdates],
          [KEYS.savedSearches, data.savedSearches],
          [KEYS.serverBuild, data.serverBuild],
          [KEYS.compare, data.comparedResources],
          [KEYS.watched, data.watchedResources],
          [KEYS.resourceSignals, data.resourceSignals]
        ];
        mappings.forEach(([key, value]) => {
          if (value !== undefined) writeJson(window.localStorage, key, value);
        });
        publish('my_lab_imported');
        window.location.reload();
      } catch (error) {
        console.error(error);
        showToast(text('This My Lab file is invalid.', 'Этот файл My Lab повреждён или имеет другой формат.'));
      }
    }, { once: true });
    input.click();
  };

  const updateDigestTags = () => {
    const interests = getInterests();
    const followed = getFollowedStudios();
    const tags = [
      'newsletter',
      'fivem-lab-weekly-digest',
      ...interests.frameworks.map((item) => `framework-${item}`),
      ...interests.types.map((item) => `interest-${item}`),
      ...(interests.price !== 'any' ? [`price-${interests.price}`] : []),
      ...followed.slice(0, 10).map((studio) => `studio-${normalize(studio.name).replace(/\s+/g, '-')}`)
    ];
    document.querySelectorAll('[data-lab-digest-tags]').forEach((input) => {
      input.value = [...new Set(tags)].join(',');
    });
  };

  const updateMyLabRetentionStats = () => {
    const saved = readJson(window.localStorage, KEYS.saved, []);
    const recent = readJson(window.localStorage, 'labViewedResourcesV1', []);
    const searches = getSavedSearches();
    const followed = getFollowedStudios();
    const newCount = personalizedNewProducts(latestCatalog).length;
    const trackedChanges = countTrackedChanges();
    const stats = {
      updates: newCount + trackedChanges,
      saved: Array.isArray(saved) ? saved.length : 0,
      recent: Array.isArray(recent) ? recent.length : 0,
      searches: searches.length,
      watching: Object.keys(watchedResources).length,
      build: serverBuild.items.length,
      studios: followed.length
    };
    Object.entries(stats).forEach(([name, value]) => {
      document.querySelectorAll(`[data-lab-my-stat="${name}"]`).forEach((target) => { target.textContent = String(value); });
    });
    document.querySelectorAll('[data-lab-my-tab-count="updates"]').forEach((target) => {
      target.textContent = String(stats.updates);
      target.hidden = stats.updates === 0;
    });
    document.querySelectorAll('[data-lab-my-tab-count="watching"]').forEach((target) => {
      target.textContent = String(trackedChanges);
      target.hidden = trackedChanges === 0;
    });
  };

  const setBuildActionStatus = (message) => {
    document.querySelectorAll('[data-lab-build-action-status]').forEach((target) => { target.textContent = message; });
  };

  const saveProfile = (form) => {
    const data = new FormData(form);
    serverBuild.profile = {
      name: String(data.get('name') || '').trim(),
      archetype: String(data.get('archetype') || 'rp'),
      framework: normalizeType(data.get('framework') || ''),
      language: String(data.get('language') || 'en'),
      budget: String(data.get('budget') || '')
    };
    saveBuild();
    document.querySelectorAll('[data-lab-server-profile]').forEach((profileForm) => {
      delete profileForm.dataset.labProfileHydrated;
    });
    renderBuild();
    setBuildActionStatus(text('Project saved on this device.', 'Проект сохранён на этом устройстве.'));
    showToast(text('Server profile saved.', 'Профиль сервера сохранён.'));
  };

  let buildDockLastFocus = null;
  let buildDockCloseTimer = null;
  const buildDockFocusable = (dock) => [...dock.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter((element) => !element.hidden && element.getClientRects().length > 0);

  const openBuildDock = (source) => {
    const dock = document.querySelector('[data-lab-build-dock]');
    if (!dock) return false;
    window.clearTimeout(buildDockCloseTimer);
    buildDockLastFocus = source || document.activeElement;
    dock.hidden = false;
    dock.setAttribute('aria-hidden', 'false');
    body.classList.add('lab-build-dock-open');
    renderBuild();
    window.requestAnimationFrame(() => {
      dock.classList.add('is-open');
      dock.querySelector('.lab-build-dock__panel')?.focus({ preventScroll: true });
    });
    publish('build_dock_opened', { itemCount: serverBuild.items.length });
    return true;
  };

  const closeBuildDock = () => {
    const dock = document.querySelector('[data-lab-build-dock]');
    if (!dock || dock.hidden) return;
    dock.classList.remove('is-open');
    dock.setAttribute('aria-hidden', 'true');
    body.classList.remove('lab-build-dock-open');
    buildDockCloseTimer = window.setTimeout(() => { dock.hidden = true; }, 280);
    if (buildDockLastFocus?.isConnected) buildDockLastFocus.focus({ preventScroll: true });
    publish('build_dock_closed', { itemCount: serverBuild.items.length });
  };

  document.addEventListener('submit', (event) => {
    const creatorSubmission = event.target.closest('#LabPublisherForm');
    if (creatorSubmission) {
      const data = new FormData(creatorSubmission);
      publish('creator_submission_started', { mode: String(data.get('contact[Request type]') || '') });
    }
    const profile = event.target.closest('[data-lab-server-profile]');
    if (!profile) return;
    event.preventDefault();
    saveProfile(profile);
  });

  document.addEventListener('click', (event) => {
    const buildDockOpen = event.target.closest('[data-lab-build-dock-open]');
    if (buildDockOpen && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      if (openBuildDock(buildDockOpen)) event.preventDefault();
      return;
    }

    if (event.target.closest('[data-lab-build-dock-close]')) {
      event.preventDefault();
      closeBuildDock();
      return;
    }

    const publishMode = event.target.closest('[data-lab-publish-mode]');
    if (publishMode) {
      const page = publishMode.closest('[data-lab-publish-page]') || document.querySelector('[data-lab-publish-page]');
      const value = publishMode.dataset.labPublishMode;
      const select = page?.querySelector('[data-lab-publish-mode-select]');
      if (select) select.value = value;
      page?.querySelectorAll('[data-lab-publish-mode]').forEach((button) => {
        const active = button.dataset.labPublishMode === value;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      page?.querySelector('.lab-publish-form-wrap')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      publish('creator_submission_mode', { mode: value });
      return;
    }

    const homeSearch = event.target.closest('[data-lab-save-home-search]');
    if (homeSearch) {
      saveSearchDefinition(createHomeSearchDefinition(), homeSearch);
      return;
    }

    const textSearch = event.target.closest('[data-lab-save-search]');
    if (textSearch) {
      saveSearchDefinition(createTextSearchDefinition(textSearch), textSearch);
      return;
    }

    const openSearch = event.target.closest('[data-lab-open-saved-search]');
    if (openSearch) {
      const searches = getSavedSearches();
      const search = searches.find((item) => item.id === openSearch.dataset.labOpenSavedSearch);
      if (search) {
        search.lastSeenAt = Date.now();
        writeJson(window.localStorage, KEYS.savedSearches, searches);
        publish('saved_search_opened', { id: search.id });
      }
      return;
    }

    const deleteSearch = event.target.closest('[data-lab-delete-saved-search]');
    if (deleteSearch) {
      const searches = getSavedSearches().filter((item) => item.id !== deleteSearch.dataset.labDeleteSavedSearch);
      writeJson(window.localStorage, KEYS.savedSearches, searches);
      renderSavedSearches();
      updateMyLabRetentionStats();
      return;
    }

    const markRead = event.target.closest('[data-lab-mark-updates-read]');
    if (markRead) {
      markUpdatesRead();
      renderNewSinceVisit();
      renderStudioUpdates();
      updateMyLabRetentionStats();
      return;
    }

    const hideStudio = event.target.closest('[data-lab-hide-studio-updates]');
    if (hideStudio) {
      const hidden = getHiddenStudios();
      hidden.add(normalize(hideStudio.dataset.labHideStudioUpdates));
      writeJson(window.localStorage, KEYS.hiddenStudios, [...hidden]);
      renderStudioUpdates();
      publish('studio_updates_hidden', { studio: hideStudio.dataset.labHideStudioUpdates });
      return;
    }

    const restoreStudio = event.target.closest('[data-lab-restore-studio-updates]');
    if (restoreStudio) {
      const hidden = getHiddenStudios();
      hidden.delete(normalize(restoreStudio.dataset.labRestoreStudioUpdates));
      writeJson(window.localStorage, KEYS.hiddenStudios, [...hidden]);
      renderStudioUpdates();
      return;
    }

    const buildButton = event.target.closest('[data-lab-build-add]');
    if (buildButton) {
      event.preventDefault();
      event.stopPropagation();
      toggleBuildProduct(buildButton.dataset.labBuildAdd, buildButton);
      return;
    }

    const removeBuild = event.target.closest('[data-lab-remove-build]');
    if (removeBuild) {
      toggleBuildProduct(removeBuild.dataset.labRemoveBuild, removeBuild);
      return;
    }

    const template = event.target.closest('[data-lab-build-template]');
    if (template) {
      applyStarterBuild(template.dataset.labBuildTemplate);
      return;
    }

    if (event.target.closest('[data-lab-clear-build]')) {
      if (!window.confirm(text('Clear every resource from this build?', 'Удалить все ресурсы из сборки?'))) return;
      serverBuild.items = [];
      saveBuild();
      renderBuild();
      return;
    }

    if (event.target.closest('[data-lab-share-build]')) {
      shareBuild();
      return;
    }

    if (event.target.closest('[data-lab-export-profile]')) {
      exportMyLab();
      return;
    }

    if (event.target.closest('[data-lab-import-profile]')) {
      importMyLabFile();
      return;
    }

    const track = event.target.closest('[data-lab-track-resource]');
    if (track) {
      event.preventDefault();
      toggleTrackedProduct(track.dataset.labTrackResource, track);
      return;
    }

    if (event.target.closest('[data-lab-clear-seen-product-changes]')) {
      markTrackedChangesSeen(true);
      showToast(text('Seen change history cleared.', 'Просмотренные изменения удалены.'));
      return;
    }

    const myLabTab = event.target.closest('[data-lab-my-tab="watching"]');
    if (myLabTab && countTrackedChanges()) {
      window.setTimeout(() => markTrackedChangesSeen(false), 500);
    }

    const compare = event.target.closest('[data-lab-compare]');
    if (compare) {
      event.preventDefault();
      event.stopPropagation();
      toggleComparedProduct(compare.dataset.labCompare, compare);
      if (compare.closest('[data-lab-compare-dialog]')) {
        const dialog = compare.closest('dialog');
        dialog?.close();
        if (comparedHandles.length >= 2) openComparison();
      }
      return;
    }

    if (event.target.closest('[data-lab-open-compare]')) {
      openComparison();
      return;
    }

    if (event.target.closest('[data-lab-clear-compare]')) {
      comparedHandles = [];
      writeJson(window.localStorage, KEYS.compare, comparedHandles);
      syncCompareButtons();
      ensureCompareTray();
      renderBuild();
      return;
    }

    const closeCompare = event.target.closest('[data-lab-close-compare]');
    if (closeCompare) {
      closeCompare.closest('dialog')?.close();
      return;
    }

    const signal = event.target.closest('[data-lab-resource-signal]');
    if (signal) toggleResourceSignal(signal);
  });

  document.addEventListener('keydown', (event) => {
    const dock = document.querySelector('[data-lab-build-dock]');
    if (!dock || dock.hidden || !dock.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeBuildDock();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = buildDockFocusable(dock);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-lab-publish-mode-select]')) return;
    const page = event.target.closest('[data-lab-publish-page]');
    page?.querySelectorAll('[data-lab-publish-mode]').forEach((button) => {
      const active = button.dataset.labPublishMode === event.target.value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  });

  const refreshRetentionControls = (scope = document) => {
    syncBuildButtons(scope);
    syncCompareButtons(scope);
    syncTrackedButtons(scope);
    updateCompatibility(scope);
    initResourceSignals(scope);
  };

  const catalogDiagnosticsEnabled = body.dataset.labDesignMode === 'true' || Boolean(window.Shopify?.designMode);
  const renderCatalogDiagnostics = () => {
    if (!catalogDiagnosticsEnabled) return;

    const records = new Map();
    document.querySelectorAll('[data-lab-resource-card]').forEach((card) => {
      const handle = card.dataset.labHandle;
      if (!handle || records.has(handle)) return;
      const missing = [];
      if (card.dataset.labCanonicalType !== 'true') missing.push('Product type');
      if (card.dataset.labCanonicalFrameworks !== 'true') missing.push('frameworks');
      if (card.dataset.labCanonicalSubcategories !== 'true') missing.push('subcategories');
      if (card.dataset.labCanonicalStudio !== 'true') missing.push('Vendor / studio');
      if (!card.dataset.labExternalUrl) missing.push('external_url');
      if (!card.dataset.labLastVerified) missing.push('last_verified_at');
      records.set(handle, {
        handle,
        title: card.dataset.labDisplayTitle || handle,
        url: card.dataset.labUrl || `${rootUrl}products/${encodeURIComponent(handle)}`,
        missing
      });
    });

    if (viewedProduct?.handle && !records.has(viewedProduct.handle)) {
      const missing = [];
      if (!viewedProduct.taxonomy?.resourceType) missing.push('Product type');
      if (!viewedProduct.taxonomy?.frameworks) missing.push('frameworks');
      if (!viewedProduct.taxonomy?.subcategories) missing.push('subcategories');
      if (!viewedProduct.taxonomy?.studio) missing.push('Vendor / studio');
      if (!viewedProduct.externalUrl) missing.push('external_url');
      if (!viewedProduct.lastVerifiedAt) missing.push('last_verified_at');
      records.set(viewedProduct.handle, {
        handle: viewedProduct.handle,
        title: viewedProduct.title,
        url: viewedProduct.url,
        missing
      });
    }

    const incomplete = [...records.values()].filter((record) => record.missing.length);
    const checkedFieldsPerRecord = 6;
    const issueCount = [...records.values()].reduce((total, record) => total + record.missing.length, 0);
    const possibleFields = Math.max(1, records.size * checkedFieldsPerRecord);
    const completeness = records.size ? Math.max(0, Math.round(((possibleFields - issueCount) / possibleFields) * 100)) : 100;
    const issueGroups = new Map();
    incomplete.forEach((record) => record.missing.forEach((field) => issueGroups.set(field, (issueGroups.get(field) || 0) + 1)));
    let panel = document.querySelector('[data-lab-catalog-diagnostics]');
    if (!panel) {
      panel = document.createElement('details');
      panel.className = 'lab-catalog-diagnostics';
      panel.dataset.labCatalogDiagnostics = '';
      document.body.append(panel);
    }

    const summary = document.createElement('summary');
    summary.textContent = incomplete.length
      ? `Catalog data: ${incomplete.length} need attention`
      : `Catalog data: ${records.size} complete`;

    const content = document.createElement('div');
    const note = document.createElement('p');
    note.textContent = 'Only visible in the Shopify theme editor. This report covers resources loaded on the current page.';
    const score = document.createElement('div');
    score.className = 'lab-catalog-diagnostics__score';
    score.innerHTML = `<strong>${completeness}%</strong><span>${records.size} checked<br>${issueCount} missing fields</span>`;
    const breakdown = document.createElement('div');
    breakdown.className = 'lab-catalog-diagnostics__breakdown';
    [...issueGroups.entries()].sort((left, right) => right[1] - left[1]).forEach(([field, count]) => {
      const item = document.createElement('span');
      item.textContent = `${field}: ${count}`;
      breakdown.append(item);
    });
    content.append(score, note, breakdown);

    if (incomplete.length) {
      const list = document.createElement('ol');
      incomplete.slice(0, 10).forEach((record) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = record.url;
        link.textContent = record.title;
        const fields = document.createElement('small');
        fields.textContent = record.missing.join(', ');
        item.append(link, fields);
        list.append(item);
      });
      content.append(list);
      if (incomplete.length > 10) {
        const remainder = document.createElement('p');
        remainder.textContent = `Plus ${incomplete.length - 10} more on the loaded page.`;
        content.append(remainder);
      }
    }

    const actions = document.createElement('div');
    actions.className = 'lab-catalog-diagnostics__actions';
    const copyReport = document.createElement('button');
    copyReport.type = 'button';
    copyReport.textContent = 'Copy issue report';
    copyReport.disabled = incomplete.length === 0;
    copyReport.addEventListener('click', async () => {
      const report = [
        `FiveM-Lab catalog report - ${new Date().toISOString()}`,
        `Completeness: ${completeness}%`,
        `Checked on this page: ${records.size}`,
        '',
        ...incomplete.map((record) => `${record.title}\t${record.url}\t${record.missing.join(', ')}`)
      ].join('\n');
      try {
        await navigator.clipboard.writeText(report);
      } catch (error) {
        const textarea = document.createElement('textarea');
        textarea.value = report;
        document.body.append(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      copyReport.textContent = 'Report copied';
      window.setTimeout(() => { copyReport.textContent = 'Copy issue report'; }, 1600);
      publish('catalog_diagnostics_copied', { records: records.size, issues: issueCount, completeness });
    });
    actions.append(copyReport);
    content.append(actions);

    panel.replaceChildren(summary, content);
  };

  refreshRetentionSurfaces = () => {
    renderNewSinceVisit(latestCatalog);
    renderStudioUpdates(latestCatalog);
    renderSavedSearches(latestCatalog);
    renderWatchedResources();
    renderBuild();
    renderSearchRecovery(latestCatalog);
    enhancePredictiveEmptyStates(latestCatalog);
    updateDigestTags();
    updateHeaderCount(latestCatalog);
    updateMyLabRetentionStats();
    refreshRetentionControls();
    ensureCompareTray();
    renderCatalogDiagnostics();
  };

  let sharedBuildHandled = false;
  const hydrateCatalog = (products) => {
    latestCatalog = [...new Map([...latestCatalog, ...products].map((product) => [product.handle, normalizeProduct(product)])).values()];
    rememberProducts(latestCatalog);
    checkWatchedProducts(latestCatalog);
    if (!sharedBuildHandled && new URL(window.location.href).searchParams.has('lab_build')) {
      sharedBuildHandled = true;
      importSharedBuild(latestCatalog);
    }
    refreshRetentionSurfaces();
  };

  initCatalogSession();
  latestCatalog = collectVisibleCardProducts();
  hydrateCatalog(latestCatalog);
  fetchCatalog().then(hydrateCatalog);

  let cardRefreshTimer;
  new MutationObserver((mutations) => {
    const addedCards = [];
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('[data-lab-resource-card]')) addedCards.push(node);
        node.querySelectorAll?.('[data-lab-resource-card]').forEach((card) => addedCards.push(card));
      });
    });
    if (!addedCards.length) return;
    window.clearTimeout(cardRefreshTimer);
    cardRefreshTimer = window.setTimeout(() => {
      const products = [...new Map(addedCards.map((card) => {
        const product = productFromCard(card);
        return [product.handle, product];
      }).filter(([handle]) => handle)).values()];
      latestCatalog = [...new Map([...latestCatalog, ...products].map((product) => [product.handle, product])).values()];
      rememberProducts(products);
      addedCards.forEach((card) => refreshRetentionControls(card));
      renderCatalogDiagnostics();
    }, 80);
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', () => window.setTimeout(renderCatalogDiagnostics, 0));

  ['interests_saved', 'interests_reset', 'studio_followed', 'studio_unfollowed', 'resource_saved', 'resource_unsaved'].forEach((name) => {
    document.addEventListener(`lab:${name}`, () => window.setTimeout(refreshRetentionSurfaces, 0));
  });
  document.addEventListener('lab:catalog_page', () => {
    catalogWasUsed = true;
    scheduleHomeStateSave(80);
  });
})();
